import { config } from '../config';

let getTokenFn: (() => Promise<string>) | null = null;

// UAT correction (2026-10-05, Builder review re-check #1): every caller-identity change in this
// app goes through setTokenProvider — AuthContext.tsx calls it whenever `getToken` changes (on
// mount, login, and after logout clears the user). Bumping this counter here, at the single real
// identity-change choke point, is what listHrAdminEmployees()'s cache (defined further below in
// this same module) keys against, so a response fetched under one caller's token can never be
// served to a different caller's session without a fresh request — see that cache's own comment
// for the full mechanism, including the in-flight-request case.
export let sessionGeneration = 0;

export function setTokenProvider(fn: () => Promise<string>) {
  getTokenFn = fn;
  sessionGeneration += 1;
}

// BUILD-046 task 8.1 (Req 2.4): lets api/workShared.ts send the same bearer token without the legacy
// refresh-then-reload behavior of apiRequest below. Additive; apiRequest is unchanged.
export async function getAuthToken(): Promise<string> {
  return getTokenFn ? await getTokenFn() : '';
}

/**
 * BUILD-039: who is signed in, and where. Used ONLY to scope the browser's record of an unresolved HQ permission change (environment + issuer + subject), so it is never
 * shown or replayed under another actor or environment. Reads the PUBLIC claims of the current token; the token itself is never stored or logged. null = unknown actor.
 */
export async function getCurrentActorScope(): Promise<string | null> {
  try {
    const token = getTokenFn ? await getTokenFn() : '';
    const part = token.split('.')[1];
    if (!part) return null;
    const json = JSON.parse(atob(part.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(part.length / 4) * 4, '=')));
    return typeof json.iss === 'string' && typeof json.sub === 'string' && json.iss && json.sub ? `${config.apiUrl}|${json.iss}|${json.sub}` : null;
  } catch { return null; }
}

// BUILD-042 (Oct 17): the HR attention counts must follow the work. Any SUCCESSFUL write under /hr/ (assign, submit, sign, review,
// correction, cancel, checklist change, ...) tells the listener to re-read the counts, so a badge never lags behind what the user just did.
// A failed write changes nothing and triggers nothing. Reads never trigger it.
let hrMutationListener: (() => void) | null = null;
export function setHrMutationListener(fn: (() => void) | null) {
  hrMutationListener = fn;
}

export async function apiRequest(path: string, options: RequestInit = {}): Promise<any> {
  const result = await apiRequestOnce(path, options);
  const method = (options.method || 'GET').toUpperCase();
  if (method !== 'GET' && path.startsWith('/hr/') && hrMutationListener) {
    try { hrMutationListener(); } catch { /* a counting problem must never fail the user's action */ }
  }
  return result;
}

async function apiRequestOnce(path: string, options: RequestInit = {}): Promise<any> {
  const token = getTokenFn ? await getTokenFn() : '';
  const response = await fetch(`${config.apiUrl}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      ...options.headers,
    },
  });

  // If 401, try refreshing the token once and retry
  if (response.status === 401 && getTokenFn) {
    const freshToken = await getTokenFn();
    if (freshToken && freshToken !== token) {
      const retryResponse = await fetch(`${config.apiUrl}${path}`, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${freshToken}`,
          ...options.headers,
        },
      });
      if (retryResponse.ok) {
        return retryResponse.json();
      }
    }
    // Token refresh didn't help — session is fully expired, redirect to login
    window.location.reload();
    throw new Error('Session expired — please sign in again');
  }

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Request failed' }));
    const err = new Error(error.message || `HTTP ${response.status}`) as Error & { status?: number; code?: string };
    // BUILD-042 UAT correction (2026-10-02): the thrown Error's `message` is the BACKEND's own
    // message text (e.g. literally "Not found" for a 404), never a string containing the status
    // code — a caller that string-matched '404' against e.message (the prior MyHrTab.tsx defect)
    // could never actually detect a 404 this way. Attach the real numeric status (and the
    // backend's error code, when present) as structured fields so callers distinguish failure
    // classes reliably instead of parsing message text.
    err.status = response.status;
    err.code = typeof error.error === 'string' ? error.error : undefined;
    // Feedback needs the earlier report's reference from a 409 REQUEST_CONFLICT body (additive; no existing caller reads it).
    (err as Error & { body?: unknown }).body = error;
    throw err;
  }

  return response.json();
}

export async function sendMessage(message: string, sessionId?: string, attachments?: Array<{name: string; type: string; content: string}>) {
  return apiRequest('/chat', {
    method: 'POST',
    body: JSON.stringify({ message, sessionId, attachments }),
  });
}

export async function getHistory(limit = 20) {
  return apiRequest(`/history?limit=${limit}`);
}

export async function getSession(sessionId: string) {
  return apiRequest(`/history/${sessionId}`);
}

export async function getDocuments(module?: string) {
  const query = module ? `?module=${module}` : '';
  return apiRequest(`/documents${query}`);
}

export async function uploadDocument(fileName: string, module: string, fileContent: string) {
  return apiRequest('/upload', {
    method: 'POST',
    body: JSON.stringify({ fileName, module, fileContent }),
  });
}

export async function deleteDocument(key: string) {
  // Key contains slashes (e.g. "shared-core/file.md") - encode each segment separately
  const encodedKey = key.split('/').map(encodeURIComponent).join('/');
  return apiRequest(`/documents/${encodedKey}`, { method: 'DELETE' });
}

// ═══════════════════════════════════════════════════════════
// WORK ITEMS API
// ═══════════════════════════════════════════════════════════

export interface WorkItem {
  id: string;
  title: string;
  description?: string;
  ownerId: string;
  ownerName: string;
  collaborators?: string[];
  approverId?: string;
  approverName?: string;
  department: string;
  status: 'active' | 'in_progress' | 'waiting' | 'blocked' | 'completed';
  priority?: WorkItemPriority;
  nextAction?: string;
  waitingOn?: string;
  waitingOnEntity?: string;
  followUpDate?: string;
  dueDate?: string;
  sourceSystem?: string;
  sourceId?: string;
  openLoopId?: string;
  projectKey?: string;
  projectDisplayName?: string;
  handoffFrom?: string;
  handoffFromName?: string;
  handoffTo?: string;
  handoffToName?: string;
  notes?: string;
  /** The latest progress note (a short copy) and when it was recorded, written by the Update form (BUILD-046 section C). */
  lastProgressText?: string;
  lastProgressAt?: string;
  lastActivityDate?: string;
  createdAt: string;
  updatedAt: string;
  completedAt?: string;
}

export interface CreateWorkItemRequest {
  title: string;
  description?: string;
  ownerId?: string;
  ownerName?: string;
  department?: string;
  status?: 'active' | 'in_progress' | 'waiting' | 'blocked';
  nextAction?: string;
  waitingOn?: string;
  waitingOnEntity?: string;
  followUpDate?: string;
  dueDate?: string;
  notes?: string;
}

export interface UpdateWorkItemRequest {
  title?: string;
  description?: string;
  status?: 'active' | 'in_progress' | 'waiting' | 'blocked' | 'completed';
  priority?: WorkItemPriority;
  nextAction?: string;
  waitingOn?: string;
  waitingOnEntity?: string;
  followUpDate?: string;
  dueDate?: string;
  notes?: string;
  /** The short reason for changing or clearing a due date or follow-up date that already had a value (Owner direction 2026-10-21). */
  changeReason?: string;
  /** Owner direction 2026-10-24: the requester's management of the assignment they issued - a job id to add or change the Customer / Job, null to clear it. Sent only when it changed. */
  jobId?: string | null;
  /** Owner direction 2026-10-24: the requester's management of the assignment they issued - a customer id to add or change the Customer, null to clear it. Sent only when it changed. */
  customerId?: string | null;
  /** Owner direction 2026-10-24: the whole collaborator list after the change (Employee IDs). Sent only when it changed. */
  collaborators?: string[];
}

export async function getWorkItems(status?: string): Promise<{ items: WorkItem[] }> {
  const query = status ? `?status=${status}` : '';
  return apiRequest(`/work${query}`);
}

export async function getTeamWorkItems(department?: string, status?: string): Promise<{ items: WorkItem[] }> {
  const params = new URLSearchParams();
  if (department) params.set('department', department);
  if (status) params.set('status', status);
  const query = params.toString() ? `?${params.toString()}` : '';
  return apiRequest(`/work/team${query}`);
}

export async function getWorkItem(id: string): Promise<{ item: WorkItem }> {
  return apiRequest(`/work/${id}`);
}

export async function createWorkItem(data: CreateWorkItemRequest): Promise<{ item: WorkItem }> {
  return apiRequest('/work', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateWorkItem(id: string, data: UpdateWorkItemRequest): Promise<{ item: WorkItem }> {
  return apiRequest(`/work/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function reassignWorkItem(id: string, newOwnerId: string, newOwnerName: string, newDepartment?: string): Promise<{ item: WorkItem }> {
  return apiRequest(`/work/${id}/reassign`, {
    method: 'POST',
    body: JSON.stringify({ newOwnerId, newOwnerName, newDepartment }),
  });
}

export async function getWorkItemHistory(id: string): Promise<{ history: Array<{ action: string; userId: string; userName: string; timestamp: string; changes?: any }> }> {
  return apiRequest(`/work/${id}/history`);
}

export async function restoreWorkItem(id: string): Promise<{ item: WorkItem; restoredTo: string }> {
  return apiRequest(`/work/${id}/restore`, { method: 'POST' });
}

// ═══════════════════════════════════════════════════════════
// MORNING BRIEF API
// ═══════════════════════════════════════════════════════════

export interface BriefData {
  text: string;
  generatedAt: string;
  weather: {
    temp: number;
    high: number;
    low: number;
    description: string;
    available: boolean;
  };
  summary: {
    active: number;
    waiting: number;
    blocked: number;
    overdue: number;
    completedToday: number;
    recentHandoffs: number;
  };
  lastAcknowledgedAt: string | null;
}

export async function getMorningBrief(refresh = false): Promise<{ brief: BriefData; cached?: boolean; cacheAge?: number }> {
  return apiRequest(`/brief${refresh ? '?refresh=true' : ''}`);
}

export async function acknowledgeBrief(): Promise<{ acknowledged: boolean; acknowledgedAt: string }> {
  return apiRequest('/brief/acknowledge', { method: 'POST' });
}

// ═══════════════════════════════════════════════════════════
// HEALTH DATA API
// ═══════════════════════════════════════════════════════════

export interface HealthIndicator {
  key: string;
  label: string;
  value: number | string;
  unit?: string;
  trend?: 'up' | 'down' | 'stable';
  role: string;
  category: string;
  description?: string;
  uploadedAt?: string;
}

export interface HealthDataResponse {
  indicators: HealthIndicator[];
  lastUpload: { uploadedAt: string; uploadedBy: string; jobCount: number; phaseCount: number } | null;
  summary: string | null;
}

export async function getHealthIndicators(): Promise<HealthDataResponse> {
  return apiRequest('/health/indicators');
}

export async function uploadHealthData(csv: string, type: string): Promise<{ success: boolean; parsed: { jobs: number; phases: number; indicators: number }; summary: string }> {
  return apiRequest('/health/upload', {
    method: 'POST',
    body: JSON.stringify({ csv, type }),
  });
}

export async function getHealthStatus(): Promise<{ lastUpload: any }> {
  return apiRequest('/health');
}

// ═══════════════════════════════════════════════════════════
// OWNER NOTES API
// ═══════════════════════════════════════════════════════════

export interface OwnerNote {
  key: string;
  title: string;
  content: string;
  category: string;
  createdAt: string;
  updatedAt: string;
}

export async function getOwnerNotes(): Promise<{ notes: OwnerNote[] }> {
  return apiRequest('/owner/notes');
}

export async function createOwnerNote(data: { title?: string; content: string; category?: string }): Promise<{ note: OwnerNote }> {
  return apiRequest('/owner/notes', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateOwnerNote(key: string, data: { title?: string; content?: string; category?: string }): Promise<{ updated: boolean }> {
  return apiRequest(`/owner/notes/${encodeURIComponent(key)}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteOwnerNote(key: string): Promise<{ deleted: boolean }> {
  return apiRequest(`/owner/notes/${encodeURIComponent(key)}`, { method: 'DELETE' });
}

// ═══════════════════════════════════════════════════════════
// HANDOFF API
// ═══════════════════════════════════════════════════════════

export interface HandoffRequest {
  targetRole: string;
  targetName: string;
  targetUserId?: string;
  targetDepartment?: string;
  title?: string;
  nextAction?: string;
  notes?: string;
}

export async function handoffWorkItem(id: string, data: HandoffRequest): Promise<{ handoffItem: WorkItem; originalCompleted: boolean; message: string }> {
  return apiRequest(`/work/${id}/handoff`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// ═══════════════════════════════════════════════════════════
// KNOWLEDGE CAPTURE API
// ═══════════════════════════════════════════════════════════

export interface KnowledgeEntry {
  id: string;
  title: string;
  content: string;
  category: string;
  status: 'pending' | 'approved';
  submittedBy: string;
  submittedAt: string;
  approvedBy?: string;
  approvedAt?: string;
}

export async function captureKnowledge(data: { content: string; context?: string; title?: string; category?: string }): Promise<{ entry: KnowledgeEntry }> {
  return apiRequest('/knowledge/capture', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function getPendingKnowledge(): Promise<{ entries: KnowledgeEntry[] }> {
  return apiRequest('/knowledge/pending');
}

export async function getApprovedKnowledge(): Promise<{ entries: KnowledgeEntry[] }> {
  return apiRequest('/knowledge/approved');
}

export async function approveKnowledge(id: string): Promise<{ approved: boolean }> {
  return apiRequest(`/knowledge/${id}/approve`, { method: 'POST' });
}

export async function rejectKnowledge(id: string, reason?: string): Promise<{ rejected: boolean }> {
  return apiRequest(`/knowledge/${id}/reject`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  });
}


// ═══════════════════════════════════════════════════════════
// ATTENTION QUEUE API (Phase 3, DEV)
// ═══════════════════════════════════════════════════════════

export type LoopBucket = 'needs-you' | 'waiting' | 'delegated' | 'conditional' | 'awareness';
export type LoopDispositionAction = 'resolve' | 'dismiss' | 'close' | 'reopen' | 'done-for-me' | 'restore-personal' | 'set-delegated';

export interface ActorRef {
  displayName: string;
  type: string;
}

export interface AttentionLoopCard {
  loopId: string;
  subject: string;
  summary: string;
  state: string;
  presentationCategory: string;
  bucket: LoopBucket;
  currentActor: ActorRef | null;
  waitingOnParty: ActorRef | null;
  delegatedTo: ActorRef | null;
  dependency: string | null;
  nextExpectedEvent: string | null;
  operationalRisk: 'none' | 'low' | 'medium' | 'high';
  urgency: number;
  importance: number;
  briefWorthy: boolean;
  personalActionComplete: boolean;
  personalQueueCleared?: boolean;
  project: { projectKey?: string; projectDisplayName: string; status: string } | null;
  evidenceCount: number;
  evidenceSource: 'inbox' | 'sent' | 'both' | null;
  createdAt: string;
  lastUpdatedAt: string;
  latestActivityAt: string;
  resolvedReason: string | null;
  promotedWorkItemId?: string | null;
  promotedWorkItemAt?: string | null;
  taskSuggestionDismissed?: boolean;
  suggestedPriority?: WorkItemPriority;
  suggestedTitle?: string;
}

export type WorkItemPriority = 'low' | 'medium' | 'high' | 'urgent';
export type NotATaskReason = 'waiting-on-someone' | 'informational' | 'already-handled' | 'delegated' | 'no-follow-up' | 'routine-notification' | 'other';

export interface PromoteTaskInput {
  title: string;
  dueDate?: string;
  priority?: WorkItemPriority;
  projectKey?: string;
  projectDisplayName?: string;
  notes?: string;
}

export interface LinkedWorkItem {
  id: string;
  title: string;
  status: string;
  priority?: WorkItemPriority;
  ownerName?: string;
  completedAt?: string;
}

export interface CompletionRecommendation {
  workItemCompleted: boolean;
  completedAt?: string;
  note: string;
  suggestions: Array<{ label: string; action: string; applies: boolean }>;
}

export interface AttentionLoopsResponse {
  groups: Record<LoopBucket, AttentionLoopCard[]>;
  archived?: AttentionLoopCard[];
  counts: Record<LoopBucket, number> & { archived: number; suppressedRoutine?: number; clearedForMe?: number };
  generatedAt: string;
  lastSyncAt?: string | null;
}

export interface AttentionLoopDetail {
  loop: AttentionLoopCard;
  resolution: {
    resolvedAt: string | null;
    resolvedReason: string | null;
    evidence: any | null;
    candidates: any[];
    personalActionComplete: boolean;
    personalActionCompleteAt: string | null;
  };
  evidence: Array<{ source: string; folder: string; timestamp: string; contribution: string; summary: string; conversationId: string; direction?: string; from?: string; to?: string; subject?: string }>;
  events: Array<{ eventType: string; description: string; detectedAt: string; closesLoop: boolean }>;
  commitments: Array<{ direction: string; who: string; description: string; status: string; dueDate: string | null }>;
  actions: Array<{ description: string; state: string; urgency: string }>;
  linkedWorkItem?: LinkedWorkItem | null;
  completionRecommendation?: CompletionRecommendation | null;
}

export async function getAttentionLoops(archived = false): Promise<AttentionLoopsResponse> {
  return apiRequest(`/attention/loops${archived ? '?archived=true' : ''}`);
}

export async function getAttentionLoop(loopId: string): Promise<AttentionLoopDetail> {
  return apiRequest(`/attention/loops/${encodeURIComponent(loopId)}`);
}

export async function disposeAttentionLoop(
  loopId: string,
  action: LoopDispositionAction,
  reason?: string,
  extra?: { delegatedToUserId?: string }
): Promise<{ loop: AttentionLoopCard }> {
  return apiRequest(`/attention/loops/${encodeURIComponent(loopId)}/disposition`, {
    method: 'POST',
    body: JSON.stringify({ action, reason, ...extra }),
  });
}

export interface TeamMember {
  userId: string;
  username: string;
  name: string;
  email: string;
}

export async function getAttentionTeam(): Promise<{ members: TeamMember[] }> {
  return apiRequest('/attention/team', { method: 'GET' });
}

export async function promoteLoopToTask(
  loopId: string,
  input: PromoteTaskInput
): Promise<{ workItem: { id: string }; loop: AttentionLoopCard }> {
  return apiRequest(`/attention/loops/${encodeURIComponent(loopId)}/promote`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function notATask(
  loopId: string,
  reason: NotATaskReason,
  note?: string
): Promise<{ ok: boolean; loop: AttentionLoopCard }> {
  return apiRequest(`/attention/loops/${encodeURIComponent(loopId)}/not-a-task`, {
    method: 'POST',
    body: JSON.stringify({ reason, note }),
  });
}

export async function checkCorrespondence(loopId: string): Promise<{ found: number; totalInConversation: number; message: string; newEvidence: Array<{ folder: string; timestamp: string; from: string }> }> {
  return apiRequest(`/attention/loops/${encodeURIComponent(loopId)}/check-correspondence`, { method: 'POST' });
}


// ═══════════════════════════════════════════════════════════
// LIBRARY API
// ═══════════════════════════════════════════════════════════

export interface LibraryResource {
  id: string;
  department: string;
  category: string;
  title: string;
  url: string | null;
  fileKey: string | null;
  fileName: string | null;
  description: string | null;
  ordinal: number;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface LibraryResponse {
  items: LibraryResource[];
  hierarchy: Record<string, Record<string, LibraryResource[]>>;
}

export interface CreateLibraryResourceRequest {
  department: string;
  category: string;
  title: string;
  url?: string;
  description?: string;
  ordinal?: number;
  active?: boolean;
}

export async function getLibraryResources(includeInactive = false): Promise<LibraryResponse> {
  const query = includeInactive ? '?include_inactive=true' : '';
  return apiRequest(`/library${query}`);
}

export async function createLibraryResource(data: CreateLibraryResourceRequest): Promise<LibraryResource> {
  return apiRequest('/library', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function uploadLibraryFile(data: {
  department: string;
  category: string;
  title: string;
  fileName: string;
  fileContent: string;
  contentType: string;
  description?: string;
  ordinal?: number;
}): Promise<LibraryResource> {
  return apiRequest('/library/upload', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function getLibraryDownloadUrl(id: string): Promise<{ downloadUrl: string; fileName: string }> {
  return apiRequest(`/library/download/${id}`);
}

export async function updateLibraryResource(id: string, data: Partial<CreateLibraryResourceRequest>): Promise<LibraryResource> {
  return apiRequest(`/library/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteLibraryResource(id: string): Promise<{ message: string }> {
  return apiRequest(`/library/${id}`, { method: 'DELETE' });
}

// ═══════════════════════════════════════════════════════════
// ADMIN USERS API
// ═══════════════════════════════════════════════════════════
// Fixes BUILD-039 Owner UAT "Failed to fetch": SettingsPage previously hand-rolled its own
// fetch() using an undefined getApiUrl() helper against a hardcoded API URL, bypassing this
// module's shared apiRequest() (token handling, 401 retry, truthful error surfacing). Routed
// through the same client as every other page so a real backend/CORS failure now surfaces a
// readable message instead of an opaque network error.

export interface AdminUserRecord {
  username: string;
  email: string;
  name: string;
  status: string;
  groups: string[];
  created: string;
  lastModified: string;
  capabilities: string[];
}

// One PAGE of HQ logins. `nextToken` is the provider's continuation token: a string when more
// pages exist, null on the last page, and ABSENT when talking to a server that predates
// pagination (which silently returns at most 60). Callers that need every login use
// loadAllLogins() in pages/usersDirectoryLoader.ts; calling this with no argument keeps the old
// first-page behaviour.
export interface AdminUsersPage {
  users: AdminUserRecord[];
  nextToken?: string | null;
  pageSize?: number;
}

export async function getAdminUsers(paginationToken?: string): Promise<AdminUsersPage> {
  const query = paginationToken ? `?paginationToken=${encodeURIComponent(paginationToken)}` : '';
  return apiRequest(`/admin/users${query}`);
}

export async function createAdminUser(data: { email: string; firstName: string; lastName: string; role: string }): Promise<{ message: string }> {
  return apiRequest('/admin/users', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function deleteAdminUser(username: string): Promise<{ message: string }> {
  return apiRequest(`/admin/users/${encodeURIComponent(username)}`, { method: 'DELETE' });
}

// ═══════════════════════════════════════════════════════════
// CANONICAL EMPLOYEE / PRODUCT ACCESS API (BUILD-039 Employee-flow phase)
// ═══════════════════════════════════════════════════════════
// Relays the platform Employee/access authority (unique-platform). This client NEVER
// recomputes status/access/Company Admin/Platform Owner — it only displays what the backend
// returns, matching the relay discipline the whole BUILD-039 authority chain follows.

export interface EmployeeIdentity {
  provider: string;
  providerPool: string;
  providerSubject: string;
}

export interface EmployeeProductDecision {
  allowed: boolean;
  reason: string;
}

export interface EmployeeRecord {
  employeeId: string;
  createdAt: string;
  identities: EmployeeIdentity[];
  globalStatus: 'active' | 'disabled' | null;
  decisions: Record<'pricing' | 'pm' | 'uniquehq', EmployeeProductDecision>;
  companyAdmin: boolean;
  platformOwner: { isOwner: boolean; isPrimary: boolean };
}

export async function getEmployees(): Promise<{ employees: EmployeeRecord[] }> {
  return apiRequest('/admin/employees');
}

export async function createEmployeeForUser(username: string): Promise<EmployeeRecord | { code: string; message: string }> {
  return apiRequest('/admin/employees', { method: 'POST', body: JSON.stringify({ username }) });
}

// BUILD-042 Owner-led onboarding (2026-10-07) — controlled DEV-fictional-fixture creation.
// Same shape as createEmployeeForUser above (an existing Cognito login for `username` must
// already exist), but records server-side provenance atomically with the new canonical
// Employee's own creation, so an HR record can later be created for it through the ordinary
// "+ Add HR record" screen with no source edit. Never usable against an existing Employee.
export async function createFictionalFixtureEmployeeForUser(username: string, note?: string): Promise<EmployeeRecord | { code: string; message: string }> {
  return apiRequest('/admin/fictional-fixture-employees', { method: 'POST', body: JSON.stringify({ username, note: note ?? null }) });
}

// Owner account-action supplement (2026-09-23): the smallest supported provider-backed
// password/account actions for one exact target login.
export async function performUserAction(
  username: string,
  action: 'require-password-change' | 'resend-invite' | 'reset-password'
): Promise<{ message: string; username: string; newStatus?: string; temporaryPassword?: string }> {
  return apiRequest(`/admin/users/${encodeURIComponent(username)}/actions`, {
    method: 'POST',
    body: JSON.stringify({ action }),
  });
}

export interface ProductAccessResult {
  employeeId: string;
  product: string;
  granted: boolean;
  provisioning: { outcome: string; cognitoUsername?: string; reason?: string } | null;
}

// BUILD-039 correction (2026-09-24): email/givenName/familyName are resolved SERVER-SIDE from the
// Employee's existing UniqueHQ identity — the backend does not read them from this request body
// at all (Builder's 2026-09-23 finding recorded the prior client-supplied-identity contract as
// leaving Pricing provisioning incomplete). This client function intentionally accepts only the
// three fields the backend actually uses.
export async function setEmployeeProductAccess(
  employeeId: string,
  product: 'pricing' | 'pm' | 'uniquehq',
  granted: boolean
): Promise<ProductAccessResult> {
  return apiRequest(`/admin/employees/${encodeURIComponent(employeeId)}/product-access`, {
    method: 'POST',
    body: JSON.stringify({ product, granted }),
  });
}

// ═══════════════════════════════════════════════════════════
// EMPLOYEE LIFECYCLE API (BUILD-039 — Deactivate / Reactivate)
// ═══════════════════════════════════════════════════════════
// Display-only: the server decides who may do this and what happened. The actor is derived server-side from the verified token;
// nothing here sends or can influence it. A refusal (409/403/400) arrives as a thrown Error carrying the backend's plain message.

export interface LifecycleLogin {
  app: string;
  account: string;
  discoveredLate: boolean;
  disable: string | null;
  signOut: string | null;
  restore: string | null;
  restoreReason: string | null;
  detail: string | null;
  /** A request to the sign-in provider was sent for this login and no answer came back (the action, or 'unknown'). */
  unsettled?: string | null;
}

export interface LifecycleStatus {
  employeeId: string;
  globalStatus: 'active' | 'disabled' | null;
  operation: { operationId: string; kind: 'deactivate' | 'reactivate'; state: string; reason: string; actorTier: string | null; requestedAt: string; completedAt: string | null } | null;
  open: boolean;
  workerActive: boolean;
  retry: { available: boolean; operationId?: string; reason?: string; since?: string };
  /** Provider requests sent and never answered. Neither time nor an observed state settles them; a Platform Owner reviews them. */
  unconfirmed?: { since: string; reviewAfterSeconds?: number; calls?: Array<{ loginId?: string; app: string; action: string | null; since?: string; reviewableAt?: string; reviewable?: boolean }> } | null;
  /** Direct account actions on this person that have not reported their result. */
  authorityReservations?: Array<{ id: string; route: string; since: string }> | null;
  enforcement: { inEffect: boolean; secondsRemaining: number; windowSeconds: number } | null;
  logins: LifecycleLogin[];
  discoveredLogins: Array<{ app: string; account: string }>;
  history: Array<{ operationId: string; kind: string; state: string; requestedAt: string }>;
}

export interface LifecycleResult {
  outcome: 'finished' | 'interrupted' | 'fenced-out';
  state?: string;
  operationId?: string;
  message?: string;
  status?: LifecycleStatus | null;
}

export async function getEmployeeLifecycle(employeeId: string): Promise<LifecycleStatus> {
  return apiRequest(`/admin/employees/${encodeURIComponent(employeeId)}/lifecycle`);
}

export async function changeEmployeeLifecycle(employeeId: string, kind: 'deactivate' | 'reactivate', reason: string): Promise<LifecycleResult> {
  return apiRequest(`/admin/employees/${encodeURIComponent(employeeId)}/${kind}`, { method: 'POST', body: JSON.stringify({ reason }) });
}

export async function retryLifecycleOperation(operationId: string): Promise<LifecycleResult> {
  return apiRequest(`/admin/lifecycle/operations/${encodeURIComponent(operationId)}/retry`, { method: 'POST', body: JSON.stringify({}) });
}

// ═══════════════════════════════════════════════════════════
// EMPLOYEE PROFILE API (BUILD-039 Phase C — profile editing)
// ═══════════════════════════════════════════════════════════
// Name-only fields for this phase (see employee-identity-service EDITABLE_PROFILE_FIELDS).
// Canonical data wins per field; a field with no canonical value yet falls back to the
// Employee's existing UniqueHQ identity (server-side), so a person is never shown as "unnamed".

export interface EmployeeProfileResponse {
  employeeId: string;
  fields: Partial<Record<'first_name' | 'last_name' | 'work_email' | 'title' | 'direct_phone' | 'mobile_phone' | 'extension' | 'photo_url', string>>;
  source: Partial<Record<string, 'canonical' | 'uniquehq-identity-fallback'>>;
  editableFields: string[];
}

export async function getEmployeeProfile(employeeId: string): Promise<EmployeeProfileResponse> {
  return apiRequest(`/admin/employees/${encodeURIComponent(employeeId)}/profile`);
}

export async function setEmployeeProfile(
  employeeId: string,
  fields: { first_name?: string; last_name?: string }
): Promise<{ employeeId: string; updated: Record<string, string> }> {
  return apiRequest(`/admin/employees/${encodeURIComponent(employeeId)}/profile`, {
    method: 'PUT',
    body: JSON.stringify(fields),
  });
}

// ───────────────────────── BUILD-043 shared Vendor Directory (UniqueHQ → Vendors) ─────────────────────────
// Company Admin management of the shared Vendor Directory, through the existing authenticated HQ admin backend. The browser never invokes a Lambda and never sends the acting
// person: the backend resolves the signed-in person to a Company Admin Employee itself. Every change carries a `clientRequestId` (one per user action, reused on a retry) so a lost
// response followed by a retry can never create anything twice. Unlike apiRequest, these calls return {status, body} instead of throwing so the screen can tell a stale edit
// (409), a refusal (403) and an outage (502) apart.
// BUILD-043 302: optional contact purpose tags (not permissions). The deterministic primary stays `isPrimary`.
export type VendorContactPurpose = 'estimating_bids' | 'scheduling_dispatch' | 'accounting' | 'project_technical';
export interface VendorContact { contactId: string; name: string | null; roleLabel: string | null; email: string | null; phone: string | null; phoneExt: string | null; isPrimary: boolean; isActive: boolean; purposes: VendorContactPurpose[] }
// BUILD-043 307: a contact the office deleted (Administer sees these; they are NOT in `contacts`) and the vendor-level deleted marker.
export interface VendorDeletedContact extends VendorContact { deletedAt: string; deletedByEmployeeId: string | null; deletedByName: string | null }
export interface VendorDeletedInfo { at: string; byEmployeeId?: string | null; byName?: string | null }
export interface VendorScopeRef { code: string; label: string; active: boolean }
export interface VendorCategoryRef { code: string; label: string; active: boolean }
export interface VendorCategoryDef { code: string; label: string; active: boolean; sortOrder: number }
// BUILD-043 302: a structured address; every part optional.
export interface VendorAddress { street1: string | null; street2: string | null; city: string | null; region: string | null; postal: string | null; country: string | null }
// BUILD-043 302: the shared business profile returned by the reader/admin views.
export interface VendorBusinessProfile {
  legalName: string | null; dbaName: string | null; businessPhone: string | null; businessEmail: string | null; website: string | null;
  businessAddress: VendorAddress | null; mailingSameAsBusiness: boolean; mailingAddress: VendorAddress | null;
}
export interface VendorRecord {
  vendorId: string; displayName: string | null; kind: 'organization' | 'internal_crew' | 'person_resource'; relationship: 'external' | 'internal';
  status: 'current' | 'review' | 'inactive'; selectable: boolean; employeeId: string | null; employeeStatus: string | null; notes: string | null; createdVia: string;
  version: number; createdAt: string; updatedAt: string; categories: VendorCategoryRef[]; scopes: VendorScopeRef[]; business: VendorBusinessProfile; contacts: VendorContact[];
  /** BUILD-043 307: administrators only. Contacts the office deleted (kept whole, restorable). Absent for View/Maintain. */
  deletedContacts?: VendorDeletedContact[];
  /** BUILD-043 307: null for a live vendor; set when the vendor was deleted (it is also always Inactive). */
  deleted?: VendorDeletedInfo | null;
}
export interface VendorHistoryEntry { seq: number; action: string; actorEmployeeId: string; actorName: string | null; requestId: string; at: string; before: any; after: any }
export interface VendorScopeDef { code: string; label: string; parentScopeCode: string | null; active: boolean; sortOrder: number; version: number }
export interface VendorCallResult<T> { ok: boolean; status: number; body: T & { error?: any; message?: string } }
// BUILD-043 302: the business-profile payload a create/update may carry (all optional).
export interface VendorProfileInput {
  legalName?: string; dbaName?: string; businessPhone?: string; businessEmail?: string; website?: string;
  businessAddress?: Partial<VendorAddress>; mailingAddress?: Partial<VendorAddress>; mailingSameAsBusiness?: boolean;
}

async function vendorRequest<T = any>(path: string, options: RequestInit = {}): Promise<VendorCallResult<T>> {
  const send = async (token: string) => fetch(`${config.apiUrl}${path}`, { ...options, headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}`, ...options.headers } });
  let token = getTokenFn ? await getTokenFn() : '';
  let response = await send(token);
  if (response.status === 401 && getTokenFn) {
    const fresh = await getTokenFn();
    if (fresh && fresh !== token) { token = fresh; response = await send(fresh); }
  }
  const body = await response.json().catch(() => ({ message: 'The server answered with something unreadable.' }));
  return { ok: response.ok, status: response.status, body };
}
/** One plain sentence for any Vendors failure (HQ errors are {error:'X', message}; directory errors are {error:{code,message}}). */
export function vendorErrorInfo(r: VendorCallResult<any>): { code: string; message: string; currentVersion?: number } {
  const e = r.body && r.body.error;
  if (e && typeof e === 'object') return { code: String(e.code || 'ERROR'), message: String(e.message || 'The request was refused.'), currentVersion: e.currentVersion };
  return { code: typeof e === 'string' ? e : `HTTP_${r.status}`, message: String((r.body && r.body.message) || 'The request failed.') };
}
const jsonBody = (b: unknown) => ({ body: JSON.stringify(b) });
export function listVendors(q: { status?: string[]; scope?: string; kind?: string; category?: string; q?: string; pageSize?: number; cursor?: string; deleted?: boolean } = {}) {
  const p = new URLSearchParams();
  if (q.status && q.status.length) p.set('status', q.status.join(','));
  if (q.deleted) p.set('deleted', 'true'); // BUILD-043 307: ONLY the deleted vendors (Administer); the ordinary list never includes them
  for (const k of ['scope', 'kind', 'category', 'q', 'cursor'] as const) if (q[k]) p.set(k, String(q[k]));
  if (q.pageSize) p.set('pageSize', String(q.pageSize));
  const qs = p.toString();
  return vendorRequest<{ vendors: VendorRecord[]; nextCursor: string | null }>(`/admin/vendors${qs ? `?${qs}` : ''}`);
}
export const getVendor = (id: string) => vendorRequest<{ vendor: VendorRecord; history: VendorHistoryEntry[] }>(`/admin/vendors/${encodeURIComponent(id)}`);
export const listVendorScopes = () => vendorRequest<{ scopes: VendorScopeDef[] }>('/admin/vendor-scopes');
export const listVendorCategories = () => vendorRequest<{ categories: VendorCategoryDef[] }>('/admin/vendor-categories');
export const createVendor = (clientRequestId: string, v: { partyKind: 'organization' | 'internal_crew'; relationship?: 'external' | 'internal'; displayName: string; notes?: string; status?: 'current' | 'review'; scopes: string[]; categories?: string[]; contacts?: Array<{ name?: string; roleLabel?: string; email?: string; phone?: string; phoneExt?: string; isPrimary?: boolean; purposes?: VendorContactPurpose[] }> } & VendorProfileInput) =>
  vendorRequest<{ vendor: VendorRecord; warnings: Array<{ code: string; message: string; vendorIds: string[] }>; replayed: boolean }>('/admin/vendors', { method: 'POST', ...jsonBody({ clientRequestId, ...v }) });
export const updateVendor = (id: string, clientRequestId: string, expectedVersion: number, v: { displayName?: string; notes?: string | null; scopes?: string[]; categories?: string[] } & VendorProfileInput) =>
  vendorRequest<{ vendor: VendorRecord; warnings: Array<{ code: string; message: string; vendorIds: string[] }> }>(`/admin/vendors/${encodeURIComponent(id)}`, { method: 'PUT', ...jsonBody({ clientRequestId, expectedVersion, ...v }) });
export const setVendorStatus = (id: string, clientRequestId: string, expectedVersion: number, status: 'current' | 'review' | 'inactive') =>
  vendorRequest<{ vendor: VendorRecord }>(`/admin/vendors/${encodeURIComponent(id)}/status`, { method: 'POST', ...jsonBody({ clientRequestId, expectedVersion, status }) });
export const setVendorScopes = (id: string, clientRequestId: string, expectedVersion: number, scopes: string[]) =>
  vendorRequest<{ vendor: VendorRecord }>(`/admin/vendors/${encodeURIComponent(id)}/scopes`, { method: 'PUT', ...jsonBody({ clientRequestId, expectedVersion, scopes }) });
export const setVendorCategories = (id: string, clientRequestId: string, expectedVersion: number, categories: string[]) =>
  vendorRequest<{ vendor: VendorRecord }>(`/admin/vendors/${encodeURIComponent(id)}/categories`, { method: 'PUT', ...jsonBody({ clientRequestId, expectedVersion, categories }) });
export const saveVendorContact = (id: string, clientRequestId: string, expectedVersion: number, c: { contactId?: string; name?: string; roleLabel?: string; email?: string; phone?: string; phoneExt?: string; isPrimary?: boolean; purposes?: VendorContactPurpose[] }) =>
  vendorRequest<{ vendor: VendorRecord }>(`/admin/vendors/${encodeURIComponent(id)}/contacts`, { method: 'POST', ...jsonBody({ clientRequestId, expectedVersion, ...c }) });
export const setVendorContactActive = (id: string, contactId: string, clientRequestId: string, expectedVersion: number, isActive: boolean) =>
  vendorRequest<{ vendor: VendorRecord }>(`/admin/vendors/${encodeURIComponent(id)}/contacts/${encodeURIComponent(contactId)}/active`, { method: 'POST', ...jsonBody({ clientRequestId, expectedVersion, isActive }) });
// BUILD-043 307: soft Delete / Restore (Administer). Nothing is erased: the record and its history are kept and Restore brings it back.
export const deleteVendorContact = (id: string, contactId: string, clientRequestId: string, expectedVersion: number) =>
  vendorRequest<{ vendor: VendorRecord }>(`/admin/vendors/${encodeURIComponent(id)}/contacts/${encodeURIComponent(contactId)}/delete`, { method: 'POST', ...jsonBody({ clientRequestId, expectedVersion }) });
export const restoreVendorContact = (id: string, contactId: string, clientRequestId: string, expectedVersion: number) =>
  vendorRequest<{ vendor: VendorRecord; restoredActive: boolean }>(`/admin/vendors/${encodeURIComponent(id)}/contacts/${encodeURIComponent(contactId)}/restore`, { method: 'POST', ...jsonBody({ clientRequestId, expectedVersion }) });
export const deleteVendorRecord = (id: string, clientRequestId: string, expectedVersion: number) =>
  vendorRequest<{ vendor: VendorRecord }>(`/admin/vendors/${encodeURIComponent(id)}/delete`, { method: 'POST', ...jsonBody({ clientRequestId, expectedVersion }) });
export const restoreVendorRecord = (id: string, clientRequestId: string, expectedVersion: number) =>
  vendorRequest<{ vendor: VendorRecord; restoredStatus: 'current' | 'review' | 'inactive'; warnings: Array<{ code: string; message: string; vendorIds: string[] }> }>(`/admin/vendors/${encodeURIComponent(id)}/restore`, { method: 'POST', ...jsonBody({ clientRequestId, expectedVersion }) });
export const saveVendorScope = (clientRequestId: string, s: { code?: string; label: string; parentScopeCode?: string | null; isActive?: boolean; expectedVersion?: number }) =>
  vendorRequest<{ scope: VendorScopeDef }>('/admin/vendor-scopes', { method: 'POST', ...jsonBody({ clientRequestId, ...s }) });

// ── BUILD-043 vendor access (View / Maintain / Administer Vendors) + bulk status. The server decides everything; the UI only reflects what it is told. ──
export type VendorLevel = 'none' | 'view' | 'maintain' | 'administer';
export interface VendorAccessMe { employeeId: string | null; level: VendorLevel; companyAdmin: boolean; canGrant: boolean }
export interface VendorAccessGrant { grantId: string; employeeId: string; name: string | null; level: Exclude<VendorLevel, 'none'>; grantedAt: string; grantedBy: string | null; revokedAt: string | null; revokedBy: string | null; active: boolean }
export const getVendorAccessMe = () => vendorRequest<VendorAccessMe>('/admin/vendor-access/me');
// grantsTruncated / peopleTruncated: the service caps the list (500 grants, 1000 people) and says so; an older service omits them (the caps are then inferred from the length)
export type VendorAccessList = { grants: VendorAccessGrant[]; people: Array<{ employeeId: string; name: string | null; companyAdmin: boolean }>; grantsTruncated?: boolean; peopleTruncated?: boolean };
export const listVendorAccess = () => vendorRequest<VendorAccessList>('/admin/vendor-access');
export const grantVendorAccess = (clientRequestId: string, employeeId: string, level: Exclude<VendorLevel, 'none'>) =>
  vendorRequest<{ grant: VendorAccessGrant; replacedLevel: string | null; replayed: boolean }>('/admin/vendor-access', { method: 'POST', ...jsonBody({ clientRequestId, employeeId, level }) });
export const revokeVendorAccess = (grantId: string, clientRequestId: string) =>
  vendorRequest<{ grant: VendorAccessGrant }>(`/admin/vendor-access/${encodeURIComponent(grantId)}/revoke`, { method: 'POST', ...jsonBody({ clientRequestId }) });
export interface BulkStatusRow { vendorId: string; outcome: 'changed' | 'already_done' | 'unchanged' | 'failed'; status?: string; version?: number; code?: string; message?: string; currentVersion?: number }
export interface BulkStatusResult { status: string; requested: number; changed: number; alreadyDone: number; unchanged: number; failed: number; results: BulkStatusRow[] }
/** One bulk change. `clientRequestId` is the BATCH id: keep it and resend it unchanged to retry (rows already done are replayed, never changed twice). */
export const bulkSetVendorStatus = (clientRequestId: string, status: 'current' | 'inactive', items: Array<{ vendorId: string; expectedVersion: number }>) =>
  vendorRequest<BulkStatusResult>('/admin/vendors/bulk-status', { method: 'POST', ...jsonBody({ clientRequestId, status, items }) });

// ── BUILD-039 Unique HQ Roles & Permissions (migration 402). The server decides every change (who may, protections, concurrency); these calls only
// carry the request. Every answer comes back as { ok, status, body } so a refusal's plain message and reason can be shown as they are. ──
export interface HqPermissionDef { key: string; module: string; label: string; description: string; kind: 'configurable' | 'owner-only'; requiresCompanyAdmin: boolean; individualGrantable: boolean; sensitive: boolean }
export interface HqRoleDef { roleId: string; code: string; label: string; description: string; isSystem: boolean; privileged: boolean; allPermissions: boolean; legacyGroup: string | null; version: number; updatedAt: string; assignedCount: number; permissions: string[] }
export interface HqViewer { companyAdmin: boolean; platformOwner: boolean; canChange: boolean }
/** Are permission WRITES open yet? Closed before the cutover: everything can be read and reviewed, nothing can be saved. */
export interface HqWrites { open: boolean; reason: 'open' | 'not-open-yet' }
export interface HqCatalog { permissions: HqPermissionDef[]; roles: HqRoleDef[]; assignments: Array<{ employeeId: string; roleId: string; version: number }>; exceptionCounts: Record<string, number>; viewer: HqViewer; writes?: HqWrites;
  retiredRoles?: Array<{ roleId: string; code: string; label: string; retiredAt: string }> }
export interface HqAccessMe { keys: string[]; role: unknown; companyAdmin: boolean; platformOwner: { isOwner: boolean; isPrimary: boolean }; source: 'platform' | 'legacy-groups'; canManageAccess: boolean }
export interface HqPersonRole { source: 'assigned' | 'sign-in-group' | 'none'; roles: Array<{ roleId: string; code: string; label: string; privileged: boolean }>; assignedAt?: string; assignedBy?: string }
export type HqEffect = 'allow' | 'block';
export interface HqPersonAccess {
  employeeId: string; role: HqPersonRole; roleDefaults: string[]; keys: string[]; version: number;
  sources: Array<{ key: string; source: 'role' | 'sign-in-group' | 'individual'; ref: string }>;
  exceptions: Array<{ exceptionId: string; key: string; effect: HqEffect; reason: string | null; at: string; by: string }>;
  history: Array<{ seq: number; at: string; actorEmployeeId: string; action: string; roleId: string | null; permission: string | null; detail: any }>;
  /** servicePrincipal = a recorded SERVICE identity (its access is not changed here). protectedIdentity is the same fact under its older name.
   *  lifecycleRetained = a retention/dependency note about a HUMAN account (it may not be deactivated); it is information, never a lock on permissions. */
  facts: { status: string | null; primaryOwner: boolean; companyAdmin: boolean; servicePrincipal?: boolean; protectedIdentity: boolean; lifecycleRetained?: { reason: string | null } | null };
  /** a legacy NAMED Vendors grant held by this person (null = none; undefined = an older server that does not report it, which is treated as unknown) */
  namedVendor?: { grantId: string; level: 'view' | 'maintain' | 'administer'; grantedBy: string | null; grantedAt: string | null; requestId: string | null } | null;
  viewer: HqViewer; writes?: HqWrites;
}
export interface HqPersonPreview {
  employeeId: string; roleId: string | null; before: string[]; after: string[]; added: string[]; removed: string[]; roleDefaults: string[];
  currentExceptions: Record<string, HqEffect>; redundantExceptions: string[]; version: number; previewToken: string;
  /** overall Vendors level (0 none, 1 view, 2 maintain, 3 administer) over ALL sources before / after this save; vendorAfterHq = from role + individual checks alone */
  vendorBefore?: number; vendorAfter?: number; vendorAfterHq?: number; companyAdmin?: boolean; absorbMismatch?: boolean;
  named?: { grantId: string; level: string; grantedBy: string | null; grantedAt: string | null; requestId: string | null } | null;
}
export interface HqRolePreview {
  roleId: string | null; added: string[]; removed: string[]; unknown: string[];
  people: Array<{ employeeId: string; via: 'assigned' | 'sign-in-group'; added: string[]; removed: string[] }>;
  unmatchedLogins: number; complete: boolean; previewToken: string;
}
/** Fields a refused (or unknown-outcome) change carries; merged into each change's answer type, so `outcome` stays a plain string here. */
export interface HqRefusal { outcome?: string; reason?: string; message?: string; permission?: string; currentVersion?: number; requestId?: string; error?: string }
export const getHqAccessMe = () => vendorRequest<HqAccessMe>('/admin/hq-access/me');
export const getHqCatalog = () => vendorRequest<HqCatalog>('/admin/hq-roles');
export const previewHqRole = (roleId: string | null, keys: string[]) => vendorRequest<HqRolePreview>('/admin/hq-roles/preview', { method: 'POST', ...jsonBody({ roleId, keys }) });
export const saveHqRole = (requestId: string, r: { roleId: string | null; label?: string; description?: string; keys: string[]; expectedVersion: number; previewToken?: string }) =>
  vendorRequest<{ roleId: string; code: string; version: number; created: boolean; replayed?: boolean } & HqRefusal>('/admin/hq-roles/save', { method: 'POST', ...jsonBody({ requestId, ...r }) });
export const retireHqRole = (requestId: string, roleId: string, expectedVersion: number) =>
  vendorRequest<{ version: number; replayed?: boolean } & HqRefusal>(`/admin/hq-roles/${encodeURIComponent(roleId)}/retire`, { method: 'POST', ...jsonBody({ requestId, expectedVersion }) });
export const getHqPersonAccess = (employeeId: string) => vendorRequest<HqPersonAccess>(`/admin/hq-people/${encodeURIComponent(employeeId)}/access`);
/** What the person would be able to do with this role (null = keep their role) and this COMPLETE exception set ({} = role defaults). Read only. */
export const previewHqPerson = (employeeId: string, roleId: string | null, exceptions: Record<string, HqEffect>, absorbNamedVendor: string | null = null) =>
  vendorRequest<HqPersonPreview>(`/admin/hq-people/${encodeURIComponent(employeeId)}/preview`, { method: 'POST', ...jsonBody({ roleId, exceptions, ...(absorbNamedVendor ? { absorbNamedVendor } : {}) }) });
/** Save the reviewed change set once. The same requestId may be sent again after a lost answer: it is never applied twice. */
export const applyHqPerson = (requestId: string, employeeId: string, change: { roleId: string | null; exceptions: Record<string, HqEffect>; expectedVersion: number; previewToken: string; reason?: string; absorbNamedVendor?: string | null }) =>
  vendorRequest<{ version: number; changes: number; added: string[]; removed: string[]; replayed?: boolean; absorbedGrant?: string | null } & HqRefusal>(
    `/admin/hq-people/${encodeURIComponent(employeeId)}/apply`, { method: 'POST', ...jsonBody({ requestId, roleId: change.roleId, exceptions: change.exceptions, expectedVersion: change.expectedVersion, previewToken: change.previewToken, ...(change.reason ? { reason: change.reason } : {}), ...(change.absorbNamedVendor ? { absorbNamedVendor: change.absorbNamedVendor } : {}) }) });
/** After a lost answer: was MY change with this request id applied? */
export const checkHqRequest = (requestId: string) =>
  vendorRequest<{ requestId: string; applied: boolean; state?: 'recorded' | 'no-record-yet' | 'refused-fenced'; final?: boolean; action?: string; at?: string; result?: any }>(`/admin/hq-requests/${encodeURIComponent(requestId)}`);

// ── BUILD-043 305 HOSTED import review (Admin tab, Administer Vendors). Staged entries are NOT vendors until a reviewer publishes them as Active or Inactive; PM never sees
// them. Every decision sends the rows the person SAW (rowId + version) and the count they confirmed; the server decides everything and answers per row. ──
export type StagedImportState = 'pending' | 'deleted' | 'published' | 'excluded';
export interface StagedImportSummary {
  displayName: string | null; kind: string; legalName: string | null; businessPhone: string | null; businessEmail: string | null; website: string | null; categories: string[];
  city: string | null; region: string | null; contactCount: number; primaryContact: { name: string | null; email: string | null; phone: string | null } | null;
}
export interface StagedImportRow {
  rowId: string; batchId: string; sourceKey: string; sourceSystem: 'proedge' | 'quickbooks'; legacyId: string | null; state: StagedImportState; version: number;
  summary: StagedImportSummary | null; flags: Array<{ code: string; detail: string | null }>; publishedVendor: { vendorId: string; displayName: string | null; status: string | null } | null;
  stagedAt: string; decidedAt: string | null; decidedBy: string | null; finalizedAt: string | null; finalizedBy: string | null;
}
export interface StagedImportTotals { pending: number; deleted: number; published: number; excluded: number }
export interface StagedImportBatch extends StagedImportTotals { batchId: string; mappingVersion: string; stagedAt: string }
export interface StagedImportList { rows: StagedImportRow[]; nextCursor: string | null; totals: StagedImportTotals; batches: StagedImportBatch[] }
export interface StagedImportDecisionRow { rowId: string; outcome: 'changed' | 'already_done' | 'failed' | 'not_attempted'; state?: StagedImportState; version?: number; vendorId?: string; code?: string; message?: string; currentVersion?: number; currentState?: StagedImportState }
export interface StagedImportDecision { op: string; status?: 'current' | 'inactive'; requested: number; changed: number; alreadyDone: number; failed: number; notAttempted?: number; results: StagedImportDecisionRow[] }
export type StagedImportAction = 'publish' | 'delete' | 'restore' | 'finalize';
export function listStagedImport(q: { batchId?: string; state?: StagedImportState | 'all'; q?: string; pageSize?: number; cursor?: string } = {}) {
  const p = new URLSearchParams();
  for (const k of ['batchId', 'state', 'q', 'cursor'] as const) if (q[k]) p.set(k, String(q[k]));
  if (q.pageSize) p.set('pageSize', String(q.pageSize));
  const qs = p.toString();
  return vendorRequest<StagedImportList>(`/admin/vendor-import${qs ? `?${qs}` : ''}`);
}
/** One review decision on the selected rows. `clientRequestId` is the BATCH id: resend it unchanged to retry (rows already done replay; nothing is done twice). */
export const decideStagedImport = (action: StagedImportAction, clientRequestId: string, items: Array<{ rowId: string; expectedVersion: number }>, status?: 'current' | 'inactive') =>
  vendorRequest<StagedImportDecision>(`/admin/vendor-import/${action}`, { method: 'POST', ...jsonBody({ clientRequestId, confirmCount: items.length, items, ...(action === 'publish' ? { status } : {}) }) });

// ── BUILD-043 import review ("Delete from import"). LOCAL import-rehearsal preview only: these routes exist nowhere else, and the screen only offers them when VITE_LOCAL_IMPORT_REHEARSAL is set. ──
export interface ImportReviewTotals { kept: number; keptByStatus: Record<string, number>; deletedFromImport: number; decidedNotYetRemoved: number; heldByImportRules: number | null }
export interface ImportDeleteRow { vendorId: string; outcome: 'deleted' | 'already_deleted' | 'blocked' | 'failed'; code?: string; message?: string; currentVersion?: number }
export interface ImportDeleteResult { requested: number; deleted: number; alreadyDeleted: number; blocked: number; failed: number; results: ImportDeleteRow[]; totals: ImportReviewTotals }
export const getImportReviewTotals = () => vendorRequest<ImportReviewTotals>('/local/import-review/summary');
/** `clientRequestId` is the batch id: keep it and resend it unchanged to retry (a decision already recorded is never recorded twice). The server also checks `confirmCount` equals the rows sent. */
export const deleteFromImport = (clientRequestId: string, items: Array<{ vendorId: string; expectedVersion: number }>) =>
  vendorRequest<ImportDeleteResult>('/local/import-review/delete', { method: 'POST', ...jsonBody({ clientRequestId, confirmCount: items.length, items }) });

// ── BUILD-043 Documents & compliance (licenses, insurance, agreements + their stored documents). Maintain Vendors and above; the server decides everything. ──
export type CoverageKind = 'general_liability' | 'auto_liability' | 'workers_comp' | 'contractor_license' | 'professional_liability' | 'umbrella_excess' | 'agreement' | 'other_license';
export type CoverageExpiryMode = 'date' | 'none' | 'unknown';
export type CoverageStatusCode = 'void' | 'renewed' | 'not_yet' | 'unknown' | 'none' | 'expired' | 'expiring' | 'current';
export interface CoverageStatus { code: CoverageStatusCode; label: string; daysLeft: number | null }
export interface CoverageFileRef { linkId: string; fileId: string; displayName: string; contentType: string; sizeBytes: number; sha256: string; storeKind: string; uploadedAt: string; linkedAt: string; unlinkedAt: string | null; active: boolean }
export interface CoverageStorage { configured: boolean; kind: string | null; label: string }
export interface CoverageRecord {
  coverageId: string; vendorId: string; kind: CoverageKind; kindLabel: string; label: string | null; issuer: string | null; reference: string | null;
  effectiveOn: string | null; expiryMode: CoverageExpiryMode; expiresOn: string | null; responsible: { employeeId: string; name: string | null; active: boolean } | null;
  notes: string | null; renewsCoverageId: string | null; state: 'active' | 'void'; voidReason: string | null; version: number; createdAt: string; updatedAt: string;
  status: CoverageStatus; files: CoverageFileRef[]; previousFiles?: CoverageFileRef[]; renewedByCoverageId?: string | null; renewalStartsOn?: string | null; historyCount?: number;
}
export interface CoverageHistoryEntry { revNo: number; action: string; actorEmployeeId: string; actorName: string | null; requestId: string; at: string; before: any; after: any }
export interface CoverageInput { kind?: CoverageKind; renewsCoverageId?: string; label?: string | null; issuer?: string | null; reference?: string | null; effectiveOn?: string | null; expiryMode?: CoverageExpiryMode; expiresOn?: string | null; responsibleEmployeeId?: string | null; notes?: string | null }
export interface ExpiringRow {
  coverageId: string; vendorId: string; vendorName: string; vendorStatus: string; kind: CoverageKind; kindLabel: string; label: string | null; issuer: string | null; reference: string | null;
  effectiveOn: string | null; expiryMode: CoverageExpiryMode; expiresOn: string | null; fileCount: number; statusCode: CoverageStatusCode; status: CoverageStatus; daysLeft: number | null;
}
const vp = (vendorId: string) => `/admin/vendors/${encodeURIComponent(vendorId)}`;
export const listCoverage = (vendorId: string) => vendorRequest<{ today: string; storage: CoverageStorage; coverage: CoverageRecord[] }>(`${vp(vendorId)}/coverage`);
export const getCoverage = (vendorId: string, coverageId: string) => vendorRequest<{ coverage: CoverageRecord; history: CoverageHistoryEntry[] }>(`${vp(vendorId)}/coverage/${encodeURIComponent(coverageId)}`);
export const createCoverage = (vendorId: string, clientRequestId: string, v: CoverageInput) =>
  vendorRequest<{ coverage: CoverageRecord; replayed: boolean }>(`${vp(vendorId)}/coverage`, { method: 'POST', ...jsonBody({ clientRequestId, ...v }) });
export const updateCoverage = (vendorId: string, coverageId: string, clientRequestId: string, expectedVersion: number, v: CoverageInput) =>
  vendorRequest<{ coverage: CoverageRecord }>(`${vp(vendorId)}/coverage/${encodeURIComponent(coverageId)}`, { method: 'PUT', ...jsonBody({ clientRequestId, expectedVersion, ...v }) });
export const voidCoverage = (vendorId: string, coverageId: string, clientRequestId: string, expectedVersion: number, reason: string) =>
  vendorRequest<{ coverage: CoverageRecord }>(`${vp(vendorId)}/coverage/${encodeURIComponent(coverageId)}/void`, { method: 'POST', ...jsonBody({ clientRequestId, expectedVersion, reason }) });
export const attachCoverageFile = (vendorId: string, coverageId: string, clientRequestId: string, expectedVersion: number, filename: string, fileData: string) =>
  vendorRequest<{ coverage: CoverageRecord; fileId: string; replayed: boolean }>(`${vp(vendorId)}/coverage/${encodeURIComponent(coverageId)}/files`, { method: 'POST', ...jsonBody({ clientRequestId, expectedVersion, filename, fileData }) });
export const unlinkCoverageFile = (vendorId: string, coverageId: string, linkId: string, clientRequestId: string, expectedVersion: number) =>
  vendorRequest<{ coverage: CoverageRecord }>(`${vp(vendorId)}/coverage/${encodeURIComponent(coverageId)}/links/${encodeURIComponent(linkId)}/unlink`, { method: 'POST', ...jsonBody({ clientRequestId, expectedVersion }) });
export const openCoverageFile = (vendorId: string, coverageId: string, fileId: string) =>
  vendorRequest<{ fileName: string; displayName: string; contentType: string; sizeBytes: number; sha256: string; linkActive: boolean; dataBase64: string; storage: { kind: string; label: string } }>(`${vp(vendorId)}/coverage/${encodeURIComponent(coverageId)}/files/${encodeURIComponent(fileId)}`);
export function listExpiring(q: { bucket?: string; kind?: string; q?: string; includeInactive?: boolean; pageSize?: number; cursor?: string } = {}) {
  const p = new URLSearchParams();
  if (q.bucket) p.set('bucket', q.bucket); if (q.kind) p.set('kind', q.kind); if (q.q) p.set('q', q.q);
  if (q.includeInactive !== undefined) p.set('includeInactive', String(q.includeInactive)); if (q.pageSize) p.set('pageSize', String(q.pageSize)); if (q.cursor) p.set('cursor', q.cursor);
  const qs = p.toString();
  return vendorRequest<{ today: string; bucket: string; total: number; items: ExpiringRow[]; nextCursor: string | null }>(`/admin/vendor-coverage/expiring${qs ? `?${qs}` : ''}`);
}

// ═══════════════════════════════════════════════════════════
// HR API (BUILD-042 Slice A)
// ═══════════════════════════════════════════════════════════

export interface HrMeResponse {
  employeeId: string;
  isPrimaryOwner: boolean;
  hasHrRecord: boolean;
  /** Oct 20: true only for an HR administrator / the Primary Owner with no HR record yet, while ordinary records are activated. Optional so an older server reads as false. */
  selfSetupAvailable?: boolean;
  capabilities: string[];
  /** `mayReviewCorrections` (Oct 19 06:28) = company-scope hr.correction.review AND existing read authority over at least one other person.
   *  It is NOT part of `hrAdmin`: a reviewer gets the correction queue and its pending-work list only. Optional so an older server reads as false. */
  surfaces: { myHr: boolean; myTeam: boolean; hrAdmin: boolean; mayReviewCorrections?: boolean; mayRequestCorrections?: boolean;
    /** Oct 23: company-scope hr.benefit.manage (effective). Reaches the benefit plan + eligibility screens only; NOT part of `hrAdmin`. Optional so an older server reads as false. */
    mayManageBenefits?: boolean;
    /** Oct 24: company-scope hr.checklist.manage / hr.checklist.assign / hr.document.manage (effective). NOT part of `hrAdmin`; optional so an older server reads as false. */
    mayManageChecklistTemplates?: boolean; mayManageEmployeeChecklists?: boolean; mayManageDocuments?: boolean };
}

export async function getHrMe(): Promise<HrMeResponse> {
  return apiRequest('/hr/me');
}

export interface HrOwnRecordResponse {
  employeeId: string;
  period: { periodNumber: number; hireDate: string; employmentStatus: string } | null;
  managerEmployeeId: string | null;
  managerName: string | null;
  termHistory: Array<{ effectiveDate: string; title: string | null; departmentId: string | null; managerEmployeeId: string | null; classification: string | null; reason?: string | null }>;
}

export async function getMyHrRecord(): Promise<HrOwnRecordResponse> {
  return apiRequest('/hr/me/record');
}

export async function getMyTeam() {
  return apiRequest('/hr/team');
}

export interface HrTeamMemberResponse {
  employeeId: string;
  name: string | null;
  managerEmployeeId: string | null;
  managerName: string | null;
  period: { periodNumber: number; hireDate: string; employmentStatus: string } | null;
  termHistory: Array<{ effectiveDate: string; title: string | null; departmentId: string | null; managerEmployeeId: string | null }>;
}

export async function getTeamMember(employeeId: string): Promise<HrTeamMemberResponse> {
  return apiRequest(`/hr/team/${encodeURIComponent(employeeId)}`);
}

export interface HrAdminEmployeeListEntry {
  employeeId: string;
  name: string | null;
  /** Work email (canonical, else the person's HQ login). Optional so an older response still parses. */
  email?: string | null;
  hasHrRecord: boolean;
  uniquehqAllowed: boolean;
  // BUILD-042 Owner-led onboarding (2026-10-07) — platform access status, distinct from HR
  // record presence or employment. Drives the active/inactive roster view filter only; never
  // an employment-termination signal.
  globalStatus: 'active' | 'disabled' | null;
  /** Oct 20: the STORED class of an existing HR record, or the class a new record would be given (from provenance only, never from the
   *  name or email). 'synthetic_fixture' is shown to people as "Test person". Optional so an older response still parses. */
  recordClass?: 'operational' | 'synthetic_fixture';
  /** Oct 20: whether HR setup can be started for this person, decided by the server BEFORE any form is shown. */
  setupEligibility?: { canCreate: boolean; kind: 'test_person' | 'ordinary'; reason: string | null };
  /** Oct 22 lifecycle: true when the person is archived from the HR roster. VISIBILITY ONLY - it says nothing about employment or access. Optional so an older server parses. */
  archived?: boolean;
  /** Oct 22 lifecycle: present ONLY for a viewer who also holds hr.lifecycle.manage. A live departure of the current employment period; null when none. */
  departure?: { state: 'departure_scheduled' | 'separated'; lastWorkingDate: string } | null;
  /** Oct 23: EFFECTIVE employment ended (today in Phoenix is after the last working date). Plain employment status for every roster reader; independent of the account. Optional so an older server parses. */
  employmentEnded?: boolean;
  /** Oct 24 guided setup: null = a legacy record or no HR record (grandfathered, never forced through setup); otherwise where the NEW record's guided setup is.
   *  Omitted by an older server. Not employment status and not an account state. */
  setup?: HrSetupRosterState | null;
}

// UAT correction (2026-10-05, Owner-observed ~15-20s Employees load): GET /hr/admin/employees
// is expensive server-side (an N+1 canonical-directory enrichment — see
// platform-employee-directory.ts, now parallelized but still non-trivial). On a single page
// visit, EmployeesPanel, EmployeePicker (rendered inside it), and separately AccessPanel each
// independently called this function, issuing 2-4 full redundant fetches for the identical
// data within the same render. This module-level cache de-duplicates CONCURRENT calls (callers
// within the same in-flight request share one Promise/one network call) and short-TTL-caches
// the resolved result so an immediately-following call (e.g. EmployeePicker mounting a moment
// after EmployeesPanel) does not re-fetch. `invalidateHrAdminEmployeesCache()` is called after
// every mutation that could change this list's content (create record, employment change,
// grant/revoke) so a caller never sees stale data after their own write.
// UAT correction (2026-10-05, Builder review re-check #1): a module-level cache keyed ONLY by
// time (not by caller identity) is a session-isolation defect — on a shared page/tab lifecycle
// (e.g. an admin signs out and a different admin signs in without a full page reload, or a
// session is otherwise swapped via setTokenProvider), a cached response fetched under the FIRST
// caller's token could be returned to the SECOND caller with no new network request, even though
// the two callers may be authorized to see different Employees. Fixed by keying the cache entry
// to an opaque "session generation" counter that increments every time setTokenProvider is
// called (every login/logout/session swap goes through it — see AuthContext.tsx) and reading
// that counter into the cache key at i) read time and ii) the moment the fetch itself starts
// (so a request already in flight when the session changes is still invalidated, including
// before THAT request resolves — the stored entry is tagged with the generation that was active
// when the fetch began, and a stale generation is never served, not even its own in-flight
// promise).
let hrAdminEmployeesCache: { promise: Promise<{ employees: HrAdminEmployeeListEntry[] }>; timestamp: number; generation: number } | null = null;
const HR_ADMIN_EMPLOYEES_CACHE_TTL_MS = 15000;

export function invalidateHrAdminEmployeesCache() {
  hrAdminEmployeesCache = null;
}

export async function listHrAdminEmployees(): Promise<{ employees: HrAdminEmployeeListEntry[] }> {
  const now = Date.now();
  const generation = sessionGeneration;
  if (hrAdminEmployeesCache && hrAdminEmployeesCache.generation === generation && now - hrAdminEmployeesCache.timestamp < HR_ADMIN_EMPLOYEES_CACHE_TTL_MS) {
    return hrAdminEmployeesCache.promise;
  }
  // includeArchived=true on purpose: this one cached call also feeds name lookups (Activity Log, pickers, attribution). Archiving hides a person from the DEFAULT
  // ROSTER only, so they must still resolve to a name everywhere else. The roster screen applies the default hiding itself, from each entry's `archived` flag.
  const promise = apiRequest('/hr/admin/employees?includeArchived=true');
  hrAdminEmployeesCache = { promise, timestamp: now, generation };
  // A failed fetch must not poison the cache for the next (possibly successful) attempt.
  promise.catch(() => { if (hrAdminEmployeesCache && hrAdminEmployeesCache.promise === promise) hrAdminEmployeesCache = null; });
  return promise;
}

/** What needs the caller's own attention (My HR). Server-scoped to the caller. */
export async function getMyHrAttention(): Promise<import('../pages/hr/workspace/attentionModel').MyAttention> {
  return apiRequest('/hr/me/attention');
}
/** What HR must act on across employees. Server-scoped to the caller's HR authority; `authorized:false` when they hold none. */
export async function getHrAdminAttention(): Promise<import('../pages/hr/workspace/attentionModel').AdminAttention> {
  return apiRequest('/hr/admin/attention');
}
/** The company-wide pending-work list behind the HR count (Oct 18). Filtered and paged on the server; totals are exact across pages. */
export async function getHrAdminPendingWork(params: { employeeId?: string; state?: string; offset?: number; limit?: number }): Promise<import('../pages/hr/workspace/attentionModel').PendingWorkPage> {
  const q = new URLSearchParams();
  if (params.employeeId) q.set('employeeId', params.employeeId);
  if (params.state && params.state !== 'all') q.set('state', params.state);
  if (params.offset) q.set('offset', String(params.offset));
  if (params.limit) q.set('limit', String(params.limit));
  const s = q.toString();
  return apiRequest(`/hr/admin/pending-work${s ? `?${s}` : ''}`);
}

// ═══════════════════════════════════════════════════════════
// EMPLOYEE PROFILE CORRECTION REQUESTS (BUILD-042, Oct 19 appendix)
// ═══════════════════════════════════════════════════════════
// A durable, permission-scoped request for HR to correct a GOVERNED fact the employee can see but
// not edit (legal name, title, department, manager, hire date, employment status). The server
// derives the current value and the field label — this client never supplies either, and the
// proposed/current values of another employee's request are never fetched here. Deciding a request
// (resolve/reject) requires the separately-grantable hr.correction.review capability server-side;
// the employee endpoints only ever act on the caller's OWN profile.
export type CorrectionRequestStatus = 'open' | 'resolved' | 'rejected';
export interface CorrectionRequestRecord {
  requestId: string;
  employeeId: string;
  fieldKey: string;
  /** Server-derived label for fieldKey, e.g. 'Job title'. */
  fieldLabel: string;
  currentValue: string | null;
  proposedValue: string;
  reason: string | null;
  status: CorrectionRequestStatus;
  requestedByEmployeeId: string;
  requestedAt: string;
  decidedByEmployeeId: string | null;
  decidedAt: string | null;
  decisionNote: string | null;
  /** Reviewer list only: resolved employee names. */
  employeeName?: string | null;
  decidedByName?: string | null;
}
/** The fixed allow-list of governed fields the caller may request a correction for (server-defined). */
export interface CorrectionFieldOption { key: string; label: string }

/** GET /hr/me/correction-requests — the caller's OWN requests (full history) + the fields they may request a correction for. */
export async function getMyCorrectionRequests(): Promise<{ requests: CorrectionRequestRecord[]; fields: CorrectionFieldOption[] }> {
  return apiRequest('/hr/me/correction-requests');
}
/** POST /hr/me/correction-requests — submit a correction about the caller's OWN profile. 201 on success; 409 if an open
 *  request for the same field already exists; 400 if the field is outside the allow-list or the proposed value is missing. */
export async function submitMyCorrectionRequest(input: { fieldKey: string; proposedValue: string; reason?: string }): Promise<CorrectionRequestRecord> {
  return apiRequest('/hr/me/correction-requests', { method: 'POST', body: JSON.stringify({ fieldKey: input.fieldKey, proposedValue: input.proposedValue, reason: input.reason ?? null }) });
}
/** GET /hr/admin/correction-requests?status=&employeeId= — the HR reviewer queue. Requires hr.correction.review (company);
 *  a caller without it receives 404 (surface not present), matching the rest of the HR admin surface. */
export async function getCorrectionRequestsForReview(params?: { status?: 'open' | 'resolved' | 'rejected' | 'all'; employeeId?: string }): Promise<{ requests: CorrectionRequestRecord[] }> {
  const q = new URLSearchParams();
  if (params?.status) q.set('status', params.status);
  if (params?.employeeId) q.set('employeeId', params.employeeId);
  const s = q.toString();
  return apiRequest(`/hr/admin/correction-requests${s ? `?${s}` : ''}`);
}
/** POST /hr/admin/correction-requests/:id/resolve — a reviewer records that the correction was handled. A note is REQUIRED,
 *  and resolving NEVER writes the profile or sign-in — HR makes any real change through the ordinary edit screens. 403 if the
 *  reviewer is the requester (no-self-decision); 409 if the request was already decided. */
export async function resolveCorrectionRequest(requestId: string, note: string): Promise<CorrectionRequestRecord> {
  return apiRequest(`/hr/admin/correction-requests/${encodeURIComponent(requestId)}/resolve`, { method: 'POST', body: JSON.stringify({ note }) });
}
/** POST /hr/admin/correction-requests/:id/reject — a reviewer declines the request. An explanation for the employee is
 *  REQUIRED. 403 on no-self-decision; 409 if already decided. */
export async function rejectCorrectionRequest(requestId: string, note: string): Promise<CorrectionRequestRecord> {
  return apiRequest(`/hr/admin/correction-requests/${encodeURIComponent(requestId)}/reject`, { method: 'POST', body: JSON.stringify({ note }) });
}

export interface HrAdminEmployeeDetailResponse {
  employeeId: string;
  name: string | null;
  managerEmployeeId: string | null;
  managerName: string | null;
  period: { periodNumber: number; hireDate: string; employmentStatus: string } | null;
  termHistory: Array<{ effectiveDate: string; title: string | null; departmentId: string | null; managerEmployeeId: string | null; classification: string | null; reason?: string | null }>;
}

export async function getHrAdminEmployee(employeeId: string): Promise<HrAdminEmployeeDetailResponse> {
  return apiRequest(`/hr/admin/employees/${encodeURIComponent(employeeId)}`);
}

export async function createHrRecord(employeeId: string, input: {
  hireDate: string; title?: string; departmentId?: string; managerEmployeeId?: string;
  classification?: string;
}) {
  // The record class is decided by the server (from recorded provenance); it is never sent from here.
  const result = await apiRequest(`/hr/admin/employees/${encodeURIComponent(employeeId)}/record`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
  invalidateHrAdminEmployeesCache(); // hasHrRecord changed for this Employee
  return result;
}

/** Oct 20: an HR administrator / the Primary Owner sets up their OWN basic HR record. Only start date, job title and department are accepted. */
export async function selfSetupHrRecord(input: { hireDate: string; title?: string; departmentId?: string }) {
  const result = await apiRequest('/hr/me/record', { method: 'POST', body: JSON.stringify(input) });
  invalidateHrAdminEmployeesCache();
  return result;
}

/** Oct 20: the company work-contact directory (name, work email, job title, department). Real data; no fallback people. */
export interface CompanyDirectoryPerson {
  employeeId: string;
  name: string;
  workEmail: string | null;
  title: string | null;
  department: string | null;
}
let companyDirectoryCache: { promise: Promise<{ people: CompanyDirectoryPerson[] }>; timestamp: number; generation: number } | null = null;
export async function getCompanyDirectory(): Promise<{ people: CompanyDirectoryPerson[] }> {
  const now = Date.now();
  const generation = sessionGeneration;
  if (companyDirectoryCache && companyDirectoryCache.generation === generation && now - companyDirectoryCache.timestamp < 15000) {
    return companyDirectoryCache.promise;
  }
  const promise = apiRequest('/hr/directory');
  companyDirectoryCache = { promise, timestamp: now, generation };
  promise.catch(() => { if (companyDirectoryCache && companyDirectoryCache.promise === promise) companyDirectoryCache = null; });
  return promise;
}

// UAT correction (2026-10-05, review finding #1): this is a PARTIAL update. The backend now
// carries forward the CURRENT value of any field this object omits entirely (distinct from
// explicitly sending it as empty/null) — see hr/index.ts's handleCreateEmploymentChange. Only
// include a field here when the caller actually intends to change it.
export async function createEmploymentChange(employeeId: string, input: {
  effectiveDate: string; title?: string; departmentId?: string; managerEmployeeId?: string;
  classification?: string; reason?: string;
}) {
  return apiRequest(`/hr/admin/employees/${encodeURIComponent(employeeId)}/employment-changes`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

/** A department as the server returns it. `disabled` is only present when the list was asked to include disabled departments. */
export interface HrDepartment { departmentId: string; name: string; disabled?: boolean }

/**
 * Departments. By default ONLY the active ones (exactly what a new selection may use: a disabled department is never offered for a new choice).
 * `{ includeDisabled: true }` returns every department with a `disabled` flag: the "Show disabled departments" view, and readable names for people who still hold one.
 */
export async function listHrDepartments(opts?: { includeDisabled?: boolean }): Promise<{ departments: HrDepartment[] }> {
  return apiRequest(opts?.includeDisabled ? '/hr/admin/departments?includeDisabled=true' : '/hr/admin/departments');
}

/** Disable a department in place (nothing is deleted; employees keep it). `changed:false` means it was already disabled. */
export async function disableHrDepartment(departmentId: string): Promise<{ departmentId: string; name: string; disabled: boolean; changed: boolean }> {
  return apiRequest(`/hr/admin/departments/${encodeURIComponent(departmentId)}/disable`, { method: 'POST' });
}

/** Re-enable a disabled department. A 409 means another active department took its name meanwhile (nothing is merged or renamed). */
export async function enableHrDepartment(departmentId: string): Promise<{ departmentId: string; name: string; disabled: boolean; changed: boolean }> {
  return apiRequest(`/hr/admin/departments/${encodeURIComponent(departmentId)}/enable`, { method: 'POST' });
}

export async function createHrDepartment(name: string) {
  return apiRequest('/hr/admin/departments', { method: 'POST', body: JSON.stringify({ name }) });
}

export async function renameHrDepartment(departmentId: string, name: string) {
  return apiRequest(`/hr/admin/departments/${encodeURIComponent(departmentId)}/rename`, { method: 'POST', body: JSON.stringify({ name }) });
}

export async function listHrAccessGrants() {
  return apiRequest('/hr/admin/access');
}

export async function createHrAccessGrant(employeeId: string, capability: string, scope: 'self' | 'direct_reports' | 'company') {
  return apiRequest('/hr/admin/access/grants', {
    method: 'POST',
    body: JSON.stringify({ employeeId, capability, scope }),
  });
}

export async function revokeHrAccessGrant(grantId: string) {
  return apiRequest(`/hr/admin/access/grants/${encodeURIComponent(grantId)}/revoke`, { method: 'POST' });
}

/** One explicit change in the one-person access editor: set a permission to a scope, or to nothing (null). */
export interface HrAccessChange { capability: string; scope: 'self' | 'direct_reports' | 'company' | null }
export interface HrAccessGrantRow { grantId: string; employeeId: string; capability: string; scope: string; grantedByEmployeeId?: string; grantedAt?: string }

/**
 * Oct 20: save several explicit changes for ONE person atomically (POST /hr/admin/access/employees/:id/changes). `requestId` makes a retry after a lost response safe;
 * `baselineGrantIds` is what the screen loaded, so a change made by someone else in the meantime is refused (409) instead of overwritten.
 */
export async function applyHrAccessChanges(employeeId: string, input: { requestId: string; baselineGrantIds: string[]; changes: HrAccessChange[] }): Promise<{ employeeId: string; replay: boolean; applied: Array<{ capability: string; from: string[]; to: string | null }>; grants: HrAccessGrantRow[] }> {
  return apiRequest(`/hr/admin/access/employees/${encodeURIComponent(employeeId)}/changes`, { method: 'POST', body: JSON.stringify(input) });
}

/** One Activity Log row exactly as GET /hr/admin/audit returns it (structural fields + labels only). */
export interface HrAuditEventRow {
  actorEmployeeId: string | null;
  actorName: string | null;
  subjectEmployeeId: string | null;
  subjectName: string | null;
  entityType: string;
  entityLabel: string;
  entityId: string | null;
  action: string;
  actionLabel: string;
  accessType: string;
  createdAt: string | null;
}
export interface HrAuditPage {
  events: HrAuditEventRow[];
  page: { limit: number; total: number; hasMore: boolean; nextCursor: string | null };
  /** false => the caller may not see employee names (ids only), as before. */
  namesAvailable: boolean;
}
export interface HrAuditQuery {
  employee?: string; actor?: string; q?: string; action?: string;
  /** Inclusive America/Phoenix calendar days, YYYY-MM-DD. */
  from?: string; to?: string;
  limit?: number; cursor?: string | null;
}

/** Server-side filtered, paged Activity Log read. Empty values are simply not sent. */
export async function getHrAudit(query: HrAuditQuery = {}): Promise<HrAuditPage> {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue;
    params.set(key, String(value));
  }
  const qs = params.toString();
  return apiRequest(`/hr/admin/audit${qs ? `?${qs}` : ''}`);
}

/** The recorded actions that exist in the caller's authorized history (for the Action filter). */
export async function getHrAuditActions(): Promise<{ actions: Array<{ action: string; label: string; count: number }> }> {
  return apiRequest('/hr/admin/audit/actions');
}

// ═══════════════════════════════════════════════════════════
// HR API — "basic HR administration" increment (2026-10-05)
// Authority: docs/superpowers/decisions/2026-10-05-build-042-approved-admin-foundation-
//            functional-dev.md
// ═══════════════════════════════════════════════════════════

export interface HrEmergencyContact {
  contactId: string;
  employeeId: string;
  name: string;
  relationship: string | null;
  phone: string;
  isPrimary: boolean;
  displayOrder: number;
}

export interface HrPersonalDetail {
  employeeId: string;
  personalEmail: string | null;
  personalPhone: string | null;
  addressLine1: string | null;
  addressCity: string | null;
  addressState: string | null;
  addressZip: string | null;
  familyReferenceNote: string | null;
  updatedAt: string | null;
  updatedByEmployeeId: string | null;
}

export interface HrPersonalDetailResponse {
  employeeId: string;
  personalDetail: HrPersonalDetail | null;
  emergencyContacts: HrEmergencyContact[];
}

export async function getHrPersonalDetail(employeeId: string): Promise<HrPersonalDetailResponse> {
  return apiRequest(`/hr/admin/employees/${encodeURIComponent(employeeId)}/personal`);
}

export async function upsertHrPersonalDetail(
  employeeId: string,
  input: Partial<{ personalEmail: string | null; personalPhone: string | null; addressLine1: string | null; addressCity: string | null; addressState: string | null; addressZip: string | null; familyReferenceNote: string | null }>
): Promise<HrPersonalDetail> {
  return apiRequest(`/hr/admin/employees/${encodeURIComponent(employeeId)}/personal`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function createHrEmergencyContact(
  employeeId: string,
  input: { name: string; relationship?: string; phone: string; isPrimary?: boolean; displayOrder?: number }
): Promise<HrEmergencyContact> {
  return apiRequest(`/hr/admin/employees/${encodeURIComponent(employeeId)}/emergency-contacts`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function archiveHrEmergencyContact(employeeId: string, contactId: string): Promise<{ contactId: string; archived: boolean }> {
  return apiRequest(`/hr/admin/employees/${encodeURIComponent(employeeId)}/emergency-contacts/${encodeURIComponent(contactId)}/archive`, { method: 'POST' });
}

// ─── Checklist templates ─────────────────────────────────────────────────────────────────────

export interface HrChecklistTemplateTask {
  taskId: string;
  taskText: string;
  // UAT local-usability cleanup (migration 085, item #6): widened from a fixed 4-value union to
  // a plain string — the set of valid labels is now HR-admin-configurable
  // (HrChecklistResponsibilityOption below), not a compile-time enum. See hr-data.ts's matching
  // comment on the backend type.
  responsibleRole: string;
  dueOffsetDays: number;
  sortOrder: number;
}
export interface HrChecklistTemplate {
  templateId: string;
  name: string;
  processKind: 'onboarding' | 'offboarding';
  retiredAt: string | null;
  /** Optional one-line "Short description" (what is involved and why to use it), max 120 characters. Absent/null on older servers = none. */
  shortDescription?: string | null;
  tasks: HrChecklistTemplateTask[];
}

export async function listHrChecklistTemplates(): Promise<{ templates: HrChecklistTemplate[] }> {
  return apiRequest('/hr/admin/checklist-templates');
}

export async function createHrChecklistTemplate(input: {
  name: string; processKind: 'onboarding' | 'offboarding';
  shortDescription?: string | null;
  tasks: Array<{ taskText: string; responsibleRole: HrChecklistTemplateTask['responsibleRole']; dueOffsetDays: number; sortOrder: number }>;
}): Promise<HrChecklistTemplate> {
  return apiRequest('/hr/admin/checklist-templates', { method: 'POST', body: JSON.stringify(input) });
}

/**
 * Replaces the template's tasks. `shortDescription`: omit = leave as is, null or empty = clear, text = set (same route, no new endpoint).
 * `name`: omit = leave the name as it is, text = RENAME the template (same route too; the server answers 400 for a blank or over-long name).
 */
export async function updateHrChecklistTemplateTasks(
  templateId: string,
  tasks: Array<{ taskText: string; responsibleRole: HrChecklistTemplateTask['responsibleRole']; dueOffsetDays: number; sortOrder: number }>,
  shortDescription?: string | null,
  name?: string
): Promise<{ templateId: string; name?: string; tasks: HrChecklistTemplateTask[]; shortDescription?: string | null }> {
  const body = {
    tasks,
    ...(shortDescription === undefined ? {} : { shortDescription }),
    ...(name === undefined ? {} : { name }),
  };
  return apiRequest(`/hr/admin/checklist-templates/${encodeURIComponent(templateId)}/tasks`, { method: 'POST', body: JSON.stringify(body) });
}

export async function retireHrChecklistTemplate(templateId: string): Promise<{ templateId: string; retired: boolean }> {
  return apiRequest(`/hr/admin/checklist-templates/${encodeURIComponent(templateId)}/retire`, { method: 'POST' });
}

/** Bring a retired template back for future use (same template: identity, tasks, order and description are kept; already-assigned checklists are never rewritten). */
export async function restoreHrChecklistTemplate(templateId: string): Promise<{ templateId: string; retired: boolean }> {
  return apiRequest(`/hr/admin/checklist-templates/${encodeURIComponent(templateId)}/restore`, { method: 'POST' });
}

// ─── Checklist assignments ───────────────────────────────────────────────────────────────────

export interface HrChecklistAssignmentTask {
  assignmentTaskId: string;
  taskText: string;
  responsibleRole: HrChecklistTemplateTask['responsibleRole'];
  dueDate: string;
  sortOrder: number;
  status: 'not_started' | 'in_progress' | 'completed' | 'exception';
  note: string | null;
  actorEmployeeId: string | null;
  statusChangedAt: string | null;
  // October 8 addendum: when set, this task's completion requires the referenced document
  // assignment to reach reviewed_complete first (server-enforced, not a UI convention).
  linkedDocumentAssignmentId: string | null;
}
export interface HrChecklistAssignment {
  assignmentId: string;
  employeeId: string;
  templateId: string;
  templateNameSnapshot: string;
  processKindSnapshot: 'onboarding' | 'offboarding';
  anchorDate: string;
  assignedByEmployeeId: string;
  assignedAt: string;
  tasks: HrChecklistAssignmentTask[];
}

export async function listHrChecklistAssignmentsForEmployee(employeeId: string): Promise<{ assignments: HrChecklistAssignment[] }> {
  return apiRequest(`/hr/admin/employees/${encodeURIComponent(employeeId)}/checklists`);
}

export async function assignHrChecklist(employeeId: string, templateId: string, anchorDate: string): Promise<HrChecklistAssignment> {
  return apiRequest(`/hr/admin/employees/${encodeURIComponent(employeeId)}/checklists`, {
    method: 'POST',
    body: JSON.stringify({ templateId, anchorDate }),
  });
}

export async function getMyHrChecklists(): Promise<{ assignments: HrChecklistAssignment[] }> {
  return apiRequest('/hr/me/checklists');
}

export async function updateHrChecklistTaskStatus(
  assignmentTaskId: string,
  input: { status: HrChecklistAssignmentTask['status']; note?: string }
): Promise<HrChecklistAssignmentTask> {
  return apiRequest(`/hr/checklist-tasks/${encodeURIComponent(assignmentTaskId)}/status`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

// October 8 addendum: HR explicitly links an existing checklist task to an existing document
// assignment (or passes null to unlink). hr.document.manage only — see index.ts's own comment
// on handleLinkChecklistTaskToDocument for why this is not gated on hr.checklist.assign.
export async function linkHrChecklistTaskToDocument(
  assignmentTaskId: string, documentAssignmentId: string | null
): Promise<HrChecklistAssignmentTask> {
  return apiRequest(`/hr/checklist-tasks/${encodeURIComponent(assignmentTaskId)}/link-document`, {
    method: 'POST',
    body: JSON.stringify({ documentAssignmentId }),
  });
}

// ─── BUILD-049: a named assignee on a checklist task (onboarding AND offboarding) ────────────────────────────────────────────
// "My assigned tasks" is the signed-in Employee's OWN rows (direct match on the stored assignee). It is the HR row itself, not a copy: every action goes to the
// same HR task. The projection is deliberately small (see assigned-tasks-routes.ts): no HR record, document, history or other checklist row.
export interface HrAssignedTask {
  assignmentTaskId: string;
  title: string;
  checklistName: string;
  checklistKind: 'onboarding' | 'offboarding';
  forEmployee: { employeeId: string; displayName: string | null };
  assignedBy: { displayName: string | null };
  dueDate: string;
  status: 'not_started' | 'in_progress' | 'completed' | 'exception';
  completedAt: string | null;
  /** Shared task instruction text, whoever wrote it. */
  taskNote: string | null;
  /** Only the fact that a document is linked. Completion then stays with the HR document review. */
  documentLinked: boolean;
  capabilities: { canStart: boolean; canNote: boolean; canComplete: boolean };
}
export interface HrAssignedTasksPage {
  tasks: HrAssignedTask[];
  page: { state: 'open' | 'done'; limit: number; offset: number; total: number; nextOffset?: number };
}
export interface HrChecklistAssigneeOverlay {
  assignmentId: string;
  assignmentTaskId: string;
  assigneeEmployeeId: string;
  assigneeName: string | null;
  /** null = could not be checked right now (never shown as "inactive" on a guess). */
  assigneeActive: boolean | null;
}

export async function getMyAssignedHrTasks(params: { state?: 'open' | 'done'; limit?: number; offset?: number } = {}): Promise<HrAssignedTasksPage> {
  const q = new URLSearchParams();
  if (params.state) q.set('state', params.state);
  if (params.limit !== undefined) q.set('limit', String(params.limit));
  if (params.offset !== undefined) q.set('offset', String(params.offset));
  const qs = q.toString();
  return apiRequest(`/hr/me/assigned-tasks${qs ? `?${qs}` : ''}`);
}

export async function progressMyAssignedHrTask(assignmentTaskId: string, input: { status?: 'in_progress' | 'completed'; note?: string | null }): Promise<{ task: HrAssignedTask }> {
  return apiRequest(`/hr/me/assigned-tasks/${encodeURIComponent(assignmentTaskId)}/progress`, { method: 'POST', body: JSON.stringify(input) });
}

/** Company-scope HR only. `assigneeEmployeeId: null` clears. One request is all-or-nothing. */
export async function setHrChecklistTaskAssignees(
  assignmentId: string, assignees: Array<{ assignmentTaskId: string; assigneeEmployeeId: string | null }>
): Promise<{ changed: Array<{ assignmentTaskId: string; assigneeEmployeeId: string | null }>; unchanged: number }> {
  return apiRequest(`/hr/admin/checklist-assignments/${encodeURIComponent(assignmentId)}/assignees`, { method: 'POST', body: JSON.stringify({ assignees }) });
}

export async function listHrChecklistAssignees(employeeId: string): Promise<{ assignees: HrChecklistAssigneeOverlay[] }> {
  return apiRequest(`/hr/admin/employees/${encodeURIComponent(employeeId)}/checklist-assignees`);
}

// ═══════════════════════════════════════════════════════════
// HR API — UAT local-usability cleanup (2026-10-05 decision), item #6
// Configurable checklist responsibility options (task-routing labels, e.g. "IT"). NOT security
// roles — see migration 085's header. NEEDS DEV DEPLOY: migration 085 + the corresponding
// backend routes have not been deployed to real DEV this pass (see checkpoint). Calling these
// against the live DEV API before that deploy will 404.
// ═══════════════════════════════════════════════════════════

export interface HrChecklistResponsibilityOption {
  optionId: string;
  label: string;
  retiredAt: string | null;
}

export async function listHrChecklistResponsibilityOptions(): Promise<{ options: HrChecklistResponsibilityOption[] }> {
  return apiRequest('/hr/admin/checklist-responsibility-options');
}

export async function createHrChecklistResponsibilityOption(label: string): Promise<HrChecklistResponsibilityOption> {
  return apiRequest('/hr/admin/checklist-responsibility-options', { method: 'POST', body: JSON.stringify({ label }) });
}

export async function renameHrChecklistResponsibilityOption(optionId: string, label: string): Promise<HrChecklistResponsibilityOption> {
  return apiRequest(`/hr/admin/checklist-responsibility-options/${encodeURIComponent(optionId)}/rename`, { method: 'POST', body: JSON.stringify({ label }) });
}

export async function retireHrChecklistResponsibilityOption(optionId: string): Promise<{ optionId: string; retired: boolean }> {
  return apiRequest(`/hr/admin/checklist-responsibility-options/${encodeURIComponent(optionId)}/retire`, { method: 'POST' });
}

// ═══════════════════════════════════════════════════════════
// HR API — Owner-led onboarding documents (2026-10-07 decision)
// HR-managed blank document templates/versions, assignment, and the paper print/sign/scan/
// upload/review workflow. Authority: docs/superpowers/decisions/
//   2026-10-07-build-042-owner-led-onboarding-documents-and-test-roster.md
// NEEDS DEV DEPLOY: migration 086 + the corresponding backend routes/HR document bucket have
// not been deployed to real DEV this pass. Calling these against the live DEV API before that
// deploy will 404/error; this is the correct fail-closed behavior, exercised via the local
// disposable walkthrough runtime instead (see scripts/local-hr-onboarding-walkthrough.mjs).
// ═══════════════════════════════════════════════════════════

export interface HrDocumentTemplateVersionMeta {
  versionId: string;
  templateId: string;
  versionNumber: number;
  fileName: string;
  contentSha256: string;
  contentType: string;
  uploadedByEmployeeId: string;
  uploadedAt: string;
}
export interface HrDocumentTemplate {
  templateId: string;
  name: string;
  description: string | null;
  createdByEmployeeId: string;
  createdAt: string;
  retiredAt: string | null;
  // October 10 14:08 addendum (migration 087) — explicit, HR-set-only classification, never
  // inferred from name/file. Required by the "Equipment returned" history reason.
  recordKind: 'equipment' | null;
  /** Cycle 12 (migration 210): HR-configured "suggest this form when setting up a new employee" flag. A suggestion only (it pre-selects, never assigns); a retired form always reads false.
   *  Optional only so older servers/fixtures still type-check; the server always sends it. */
  suggestedForOnboarding?: boolean;
  versions: HrDocumentTemplateVersionMeta[];
}

export async function listHrDocumentTemplates(): Promise<{ templates: HrDocumentTemplate[] }> {
  return apiRequest('/hr/admin/document-templates');
}

/** October 10 14:05 addendum — confirmed, audited HR Reactivate of a retired Form Library
 *  template. Never auto-assigns the form or restores any employee record. */
export async function reactivateHrDocumentTemplate(templateId: string): Promise<{ templateId: string; reactivated: boolean }> {
  return apiRequest(`/hr/admin/document-templates/${encodeURIComponent(templateId)}/reactivate`, { method: 'POST' });
}

export async function createHrDocumentTemplate(input: { name: string; description?: string; fileName: string; fileContentBase64: string; contentType: string; recordKind?: 'equipment' | null; suggestedForOnboarding?: boolean }): Promise<HrDocumentTemplate> {
  return apiRequest('/hr/admin/document-templates', { method: 'POST', body: JSON.stringify(input) });
}

/** Edit form dialog (approved) — metadata-only update of title/description. Creates NO new
 *  version; earlier versions and every employee assignment/record are untouched. A file
 *  replacement is the separate createHrDocumentTemplateVersion call. */
export async function updateHrDocumentTemplate(templateId: string, input: { name: string; description?: string; suggestedForOnboarding?: boolean }): Promise<HrDocumentTemplate> {
  // Cycle 12: `suggestedForOnboarding` is sent ONLY when the caller supplies it (omitted = unchanged on the server).
  return apiRequest(`/hr/admin/document-templates/${encodeURIComponent(templateId)}/update`, { method: 'POST', body: JSON.stringify({ name: input.name, description: input.description ?? '', ...(typeof input.suggestedForOnboarding === 'boolean' ? { suggestedForOnboarding: input.suggestedForOnboarding } : {}) }) });
}

export async function createHrDocumentTemplateVersion(templateId: string, input: { fileName: string; fileContentBase64: string; contentType: string }): Promise<HrDocumentTemplateVersionMeta> {
  return apiRequest(`/hr/admin/document-templates/${encodeURIComponent(templateId)}/versions`, { method: 'POST', body: JSON.stringify(input) });
}

export async function retireHrDocumentTemplate(templateId: string): Promise<{ templateId: string; retired: boolean }> {
  return apiRequest(`/hr/admin/document-templates/${encodeURIComponent(templateId)}/retire`, { method: 'POST' });
}

// October 8 22:34 Phoenix addendum ("Form Library"): preview/download a blank Library form
// directly from the Library, independent of any employee assignment.
// October 10 12:24 addendum: Preview opens the SAME file inline in a new tab (disposition=
// inline); Download forces a named save (disposition=attachment, the default/prior behavior
// when omitted). Same route, same bytes, same authorization — only the disposition differs.
export async function downloadHrDocumentTemplateVersion(templateId: string, versionId: string, disposition: 'inline' | 'attachment' = 'attachment'): Promise<{ url: string; fileName: string; expiresInSeconds: number }> {
  return apiRequest(`/hr/admin/document-templates/${encodeURIComponent(templateId)}/versions/${encodeURIComponent(versionId)}/download?disposition=${disposition}`);
}

export interface HrDocumentSubmissionMeta {
  submissionId: string;
  assignmentId: string;
  fileName: string;
  contentSha256: string;
  contentType: string;
  uploadedByEmployeeId: string;
  uploadedAt: string;
  reportedSignedDate: string | null;
  reviewedByEmployeeId: string | null;
  reviewedAt: string | null;
  reviewOutcome: 'approved' | 'needs_correction' | null;
  reviewNote: string | null;
  supersededAt: string | null;
}
export interface HrDocumentAssignment {
  assignmentId: string;
  employeeId: string;
  templateId: string;
  templateVersionId: string;
  assignedByEmployeeId: string;
  assignedAt: string;
  // Owner-specified vocabulary: awaiting_return (nothing uploaded yet), awaiting_hr_review (a
  // scan was uploaded, HR has not reviewed it), needs_correction (HR found a problem),
  // reviewed_complete (HR reviewed and accepted it). Upload alone never produces
  // reviewed_complete. 'cancelled' added October 8 08:19/08:20 addenda — a reversible HR
  // correction for a MISTAKEN outstanding assignment, never a hard delete.
  status: 'awaiting_return' | 'awaiting_hr_review' | 'needs_correction' | 'reviewed_complete' | 'cancelled';
  currentSubmissionId: string | null;
  updatedAt: string;
  templateName: string;
  versionNumber: number;
  fileName: string;
  submissions: HrDocumentSubmissionMeta[];
  cancelledAt: string | null;
  cancelledByEmployeeId: string | null;
  cancelReason: string | null;
  // Migration 087 — archive PLACEMENT only, independent of status.
  movedToHistoryAt: string | null;
  movedToHistoryByEmployeeId: string | null;
  currentHistoryEventId: string | null;
  // BUILD-042 equipment checkout: present only on an assignment created by Generate & assign.
  equipmentCheckout?: {
    checkoutId: string;
    issueDate: string;
    itemCount: number;
    witnessRequired: boolean;
    witnessEmployeeId: string | null;
    signing: HrSigningRequestSummary | null;
  };
  // BUILD-042 (Oct 15 22:12): present only on an ordinary uploaded-PDF form whose assignment was pinned to a signing setup.
  documentSigning?: {
    configId: string;
    configNumber: number;
    pageCount: number;
    signing: HrSigningRequestSummary | null;
  };
}

export async function getMyHrDocuments(): Promise<{ assignments: HrDocumentAssignment[] }> {
  return apiRequest('/hr/me/documents');
}

export async function listHrDocumentAssignmentsForEmployee(employeeId: string): Promise<{ assignments: HrDocumentAssignment[] }> {
  return apiRequest(`/hr/admin/employees/${encodeURIComponent(employeeId)}/documents`);
}

export async function assignHrDocument(employeeId: string, templateId: string, templateVersionId: string, fieldValues?: Record<string, string>): Promise<HrDocumentAssignment> {
  return apiRequest(`/hr/admin/employees/${encodeURIComponent(employeeId)}/documents`, {
    method: 'POST',
    // fieldValues: only the values HR types for an expanded-fields form (see HrAssignmentPrompt). Never anything an employee types.
    body: JSON.stringify({ templateId, templateVersionId, ...(fieldValues && Object.keys(fieldValues).length ? { fieldValues } : {}) }),
  });
}

// October 10 12:24 addendum: same Preview (inline) vs Download (attachment) separation as the
// Library's own version download, applied to an employee's assigned blank form.
export async function downloadHrDocumentTemplate(assignmentId: string, disposition: 'inline' | 'attachment' = 'attachment'): Promise<{ url: string; fileName: string; expiresInSeconds: number }> {
  return apiRequest(`/hr/documents/${encodeURIComponent(assignmentId)}/template/download?disposition=${disposition}`);
}

// October 8 08:19/08:20 addenda: a clear, reversible Cancel action for a mistaken outstanding
// assignment. reason is optional free text retained on the record, never a delete.
export async function cancelHrDocumentAssignment(assignmentId: string, reason?: string): Promise<HrDocumentAssignment> {
  return apiRequest(`/hr/documents/${encodeURIComponent(assignmentId)}/cancel`, {
    method: 'POST',
    body: JSON.stringify({ reason: reason ?? null }),
  });
}

export async function uploadHrDocumentSubmission(assignmentId: string, input: { fileName: string; fileContentBase64: string; contentType: string; reportedSignedDate?: string }): Promise<HrDocumentSubmissionMeta> {
  return apiRequest(`/hr/documents/${encodeURIComponent(assignmentId)}/submissions`, { method: 'POST', body: JSON.stringify(input) });
}

// October 10 12:24 addendum: same Preview (inline) vs Download (attachment) separation, applied
// to an uploaded completed scan.
export async function downloadHrDocumentSubmission(submissionId: string, disposition: 'inline' | 'attachment' = 'attachment'): Promise<{ url: string; fileName: string; expiresInSeconds: number }> {
  return apiRequest(`/hr/documents/submissions/${encodeURIComponent(submissionId)}/download?disposition=${disposition}`);
}

export async function reviewHrDocumentSubmission(submissionId: string, outcome: 'approved' | 'needs_correction', note?: string): Promise<{ submission: HrDocumentSubmissionMeta; assignmentId: string }> {
  return apiRequest(`/hr/documents/submissions/${encodeURIComponent(submissionId)}/review`, { method: 'POST', body: JSON.stringify({ outcome, note: note ?? null }) });
}

// ═══════════════════════════════════════════════════════════
// HR API — Equipment checkout + prepared e-signature (BUILD-042, Oct 15 08:13)
// LOCAL IMPLEMENTATION: migration 101 is not applied and no signing provider is connected.
// ═══════════════════════════════════════════════════════════
export interface HrEquipmentItem {
  position?: number;
  equipmentType: string;
  description: string;
  model: string | null;
  serialOrAsset: string | null;
  notes: string | null;
}
export interface HrSigningRequestSummary {
  requestId: string;
  status: 'prepared' | 'awaiting_signers' | 'completed' | 'cancelled' | 'failed';
  employeeSigned: boolean;
  witnessRequired: boolean;
  witnessSigned: boolean;
  createdAt: string;
  completedAt: string | null;
  cancelledAt: string | null;
  failureReason: string | null;
  /** The BoldSign environment that created this request ('sandbox' = test only). Fixed when the request was created. */
  providerEnvironment?: 'sandbox' | 'live';
}
export interface HrEquipmentCheckout {
  checkoutId: string;
  employeeId: string;
  status: 'draft' | 'generated' | 'cancelled';
  issueDate: string;
  witnessEmployeeId: string | null;
  createdAt: string;
  updatedAt: string;
  assignmentId: string | null;
  generatedFileName: string | null;
  generatedPageCount: number | null;
  items: HrEquipmentItem[];
  assignmentStatus: HrDocumentAssignment['status'] | null;
  signing: HrSigningRequestSummary | null;
}
export interface HrEquipmentCheckoutList {
  checkouts: HrEquipmentCheckout[];
  equipmentTypes: string[];
  limits: { maxItems: number; maxDescription: number; maxModel: number; maxSerialOrAsset: number; maxNotes: number };
}
// ── Equipment & Property schedule (read-only; derived server-side from generated equipment checkouts) ──────────────────
export type HrPropertyState = 'awaiting_signature' | 'signed_awaiting_review' | 'needs_correction' | 'reviewed_complete' | 'cancelled' | 'history';
export interface HrPropertyDocument {
  /** 'signed_copy' = the retained signed copy; 'unsigned_assigned_copy' = the employee-specific form as issued; 'unavailable' = expected but missing. */
  kind: 'signed_copy' | 'unsigned_assigned_copy' | 'unavailable';
  assignmentId: string;
  submissionId: string | null;
  label: string;
}
export interface HrPropertyItem {
  itemKey: string; checkoutId: string; assignmentId: string; position: number;
  equipmentType: string; description: string; model: string | null; serialOrAsset: string | null; notes: string | null;
  issueDate: string; state: HrPropertyState; signedVia: 'electronic' | 'paper_upload' | null; document: HrPropertyDocument;
}
export interface HrPropertySchedule {
  employeeId: string; source: 'generated_equipment_checkouts';
  counts: { onRecord: number; awaitingSignature: number; signedAwaitingReview: number; reviewedComplete: number; notCounted: number; checkouts: number };
  items: HrPropertyItem[]; otherItems: HrPropertyItem[]; semantics: string[];
}
export async function getMyHrEquipmentProperty(): Promise<HrPropertySchedule> {
  return apiRequest('/hr/me/equipment-property');
}
export async function getHrEquipmentPropertyForEmployee(employeeId: string): Promise<HrPropertySchedule> {
  return apiRequest(`/hr/admin/employees/${encodeURIComponent(employeeId)}/equipment-property`);
}

export async function listHrEquipmentCheckouts(employeeId: string): Promise<HrEquipmentCheckoutList> {
  return apiRequest(`/hr/admin/employees/${encodeURIComponent(employeeId)}/equipment-checkouts`);
}
// Witnesses were removed from new equipment checkouts (Oct 15 19:25): the server rejects any witness input, so none is sent.
// The witness fields on the response types below exist only to describe checkouts issued before that change.
export async function saveHrEquipmentCheckoutDraft(employeeId: string, input: { checkoutId?: string | null; issueDate: string; items: HrEquipmentItem[] }): Promise<HrEquipmentCheckout> {
  return apiRequest(`/hr/admin/employees/${encodeURIComponent(employeeId)}/equipment-checkouts`, { method: 'POST', body: JSON.stringify(input) });
}
export async function discardHrEquipmentCheckoutDraft(checkoutId: string): Promise<HrEquipmentCheckout> {
  return apiRequest(`/hr/admin/equipment-checkouts/${encodeURIComponent(checkoutId)}/discard`, { method: 'POST', body: '{}' });
}
export async function generateHrEquipmentCheckout(checkoutId: string): Promise<{ checkout: HrEquipmentCheckout; assignment: HrDocumentAssignment }> {
  return apiRequest(`/hr/admin/equipment-checkouts/${encodeURIComponent(checkoutId)}/generate`, { method: 'POST', body: '{}' });
}

export interface HrSigningStatus {
  assignmentId: string;
  assignmentStatus: HrDocumentAssignment['status'];
  providerConfigured: boolean;
  /** false for the local disconnected preview: the UI must say NOT CONNECTED. */
  providerConnected: boolean;
  providerName: string | null;
  /** 'sandbox' = real BoldSign SANDBOX (test only); 'live' = real account; 'preview' = local simulation. */
  providerEnvironment?: 'sandbox' | 'live' | 'preview' | null;
  /** What a NEW signing request would use right now (null = not recognised, so new requests are refused). */
  configuredEnvironment?: 'sandbox' | 'live' | null;
  witnessRequired: boolean;
  /** 'equipment' = generated equipment checkout; 'document' = ordinary uploaded-PDF form with a pinned signing setup. */
  documentKind?: 'equipment' | 'document';
  callerIsEmployee: boolean;
  callerIsWitness: boolean;
  canStartEmployeeSigning: boolean;
  canSignAsWitness: boolean;
  request: HrSigningRequestSummary | null;
}
export async function getHrDocumentSigning(assignmentId: string): Promise<HrSigningStatus> {
  return apiRequest(`/hr/documents/${encodeURIComponent(assignmentId)}/signing`);
}
export async function startHrDocumentSigning(assignmentId: string, role: 'employee' | 'witness', returnUrl: string): Promise<{ signLink: string; role: string; providerConnected: boolean; providerName: string }> {
  return apiRequest(`/hr/documents/${encodeURIComponent(assignmentId)}/signing/session`, { method: 'POST', body: JSON.stringify({ role, returnUrl }) });
}
export async function syncHrDocumentSigning(assignmentId: string): Promise<{ request: HrSigningRequestSummary | null; assignmentStatus: HrDocumentAssignment['status'] | null }> {
  return apiRequest(`/hr/documents/${encodeURIComponent(assignmentId)}/signing/sync`, { method: 'POST', body: '{}' });
}
export async function cancelHrDocumentSigning(assignmentId: string, reason?: string): Promise<{ cancelled: boolean; request: HrSigningRequestSummary | null }> {
  return apiRequest(`/hr/documents/${encodeURIComponent(assignmentId)}/signing/cancel`, { method: 'POST', body: JSON.stringify({ reason: reason ?? null }) });
}

// ═══════════════════════════════════════════════════════════
// HR API — Form Library "Set up signing" (BUILD-042, Oct 15 22:12). hr.document.manage only.
// One employee signature box + one date box per uploaded-PDF VERSION; coordinates are PDF points, origin bottom-left, pages 1-based.
// ═══════════════════════════════════════════════════════════
export interface HrPlacementBox { x: number; y: number; width: number; height: number }
export interface HrDocumentPlacement {
  employee: { signaturePage: number; signature: HrPlacementBox; datePage: number; dateSigned: HrPlacementBox };
}
// Expanded form fields (BUILD-042, Oct 16). One employee, many fields. Same coordinate rules as above.
export type HrFormFieldKind = 'signature' | 'initial' | 'dateSigned' | 'text' | 'dropdown' | 'checkbox' | 'radio';
export type HrFormFieldSource = 'employee' | 'profile' | 'assignment' | 'fixed';
export interface HrFormField {
  fieldKey: string;
  kind: HrFormFieldKind;
  page: number;
  box: HrPlacementBox;
  label: string;
  required: boolean;
  source: HrFormFieldSource;
  profileKey?: 'employeeName' | 'employeeWorkEmail';
  /** Authoritative text the employee sees READ-ONLY (fixed source only). */
  fixedValue?: string;
  /** Read-only for the employee. Derived by the server for every value HR sets; never a free choice. */
  locked?: boolean;
  /** EDITABLE starting answer of an employee text box (the employee may change or clear it). Not a fixed value. */
  startingValue?: string;
  text?: { multiline: boolean; maxLength: number; validation: string };
  dropdown?: { options: string[]; defaultValue?: string };
  radio?: { groupKey: string; optionValue: string };
  origin?: { kind: 'authored' | 'imported' | 'initials-set' | 'initials-override'; acroName?: string; importId?: string; setKey?: string; note?: string };
}
export interface HrInitialsSet {
  setKey: string;
  label: string;
  required: boolean;
  anchor: HrPlacementBox;
  pages: 'all' | number[];
  excludePages: number[];
  overrides: { page: number; box: HrPlacementBox }[];
}
export interface HrFormSetupBody { fields: HrFormField[]; initialsSets: HrInitialsSet[] }
export interface HrFormSetupStored extends HrFormSetupBody { schemaVersion: 3 }
export interface HrImportItem {
  id: string; acroName: string; pdfKind: string; page: number | null; box: HrPlacementBox | null;
  status: 'imported' | 'imported-with-note' | 'unsupported'; notes: string[]; requiredInPdf: boolean; calculated: boolean; fieldKeys: string[]; blocking: boolean;
}
export interface HrImportReport { acroFieldCount: number; items: HrImportItem[]; hasXfa: boolean; hybridNote?: string; counts: { imported: number; importedWithNote: number; unsupported: number; blocking: number } }
export type HrImportResolution = { kind: 'replaced'; fieldKey: string } | { kind: 'ack' };
export interface HrImportPreview {
  fillable: boolean; report: HrImportReport | null; draftFields: HrFormField[]; preparedBase64: string | null; preparedProblem?: string | null;
  /** Why the prepared copy could not be built (a stable code and the library's own words); null when it was built. */
  preparedDiagnostic?: { code: string; detail: string } | null; originalSha256?: string; message?: string;
}
/** Oct 17: the saved, UNPUBLISHED draft of a version's setup. It is authoring state only; nothing assignable reads it. */
export interface HrSigningDraft {
  revision: number; savedAt: string; savedByName: string | null; baseConfigId: string | null;
  /** True when a different setup has been published since this draft was last saved. */
  liveChangedSince: boolean;
  body: { schemaVersion: 1; fields: HrFormField[]; initialsSets: HrInitialsSet[]; importResolutions: Record<string, HrImportResolution> };
}
/** Who supplies the rest of a form (Oct 17): labels only, never a value or a format. */
export interface HrAssignmentSummary { employeeSupplies: Array<{ label: string; kind: string; required: boolean; startsWithAnswer?: boolean }>; recordSupplies: Array<{ label: string }>; fixedSupplies?: Array<{ label: string }> }
export interface HrAssignmentPrompt { fieldKey: string; label: string; kind: HrFormFieldKind; required: boolean; options: string[] | null; validation: string | null; maxLength: number | null }
export interface HrEditorRules {
  schemaVersion: number; multilineConfirmed: boolean; multilineLimits?: { minBoxWidthPts: number; minBoxHeightPts: number; maxLines: number; maxCharacters: number }; maxFields: number; maxTextLength: number; defaultTextLength: number;
  profileKeys: string[]; textValidations: Record<string, string>;
}

export interface HrSigningConfig {
  configId: string;
  templateId: string;
  templateVersionId: string;
  configNumber: number;
  contentSha256: string;
  pageCount: number;
  pageSizes: { width: number; height: number }[];
  placement: HrDocumentPlacement | HrFormSetupStored;
  attestationKind?: 'signature-date-only' | 'fields-reviewed';
  fieldCount?: number | null;
  hasPreparedCopy?: boolean;
  preparedContentSha256?: string | null;
  importReport?: { counts?: HrImportReport['counts']; resolutions?: Record<string, HrImportResolution> } | null;
  placementSchemaVersion: number;
  scopeConfirmed: boolean;
  createdByEmployeeId: string;
  createdAt: string;
  retiredAt: string | null;
  retiredByEmployeeId: string | null;
  createdByName?: string | null;
  retiredByName?: string | null;
}
export interface HrSigningSetup {
  template: { templateId: string; name: string; retiredAt: string | null; recordKind: 'equipment' | null };
  version: { versionId: string; versionNumber: number; fileName: string; contentType: string; contentSha256: string };
  limits: {
    signature: { minWidth: number; minHeight: number; maxWidthFraction: number; maxHeightFraction: number };
    date: { minWidth: number; minHeight: number; maxWidthFraction: number; maxHeightFraction: number };
    maxPages: number;
    placementSchemaVersion: number;
  };
  scopeStatement: string;
  eligibility: { ok: boolean; problems: string[]; pageCount: number; pages: { width: number; height: number }[] };
  liveConfig: HrSigningConfig | null;
  /** Oct 17: the saved, unpublished draft, or null. */
  draft?: HrSigningDraft | null;
  history: HrSigningConfig[];
  /** The exact stored PDF (base64) when it can be set up with the legacy signature+date screen; null otherwise (including fillable PDFs). */
  fileBase64: string | null;
  /** Expanded fields (Oct 16): what the version-3 editor uses. `fileBase64` here is the exact stored PDF even when it is fillable. */
  expanded?: {
    rules: HrEditorRules;
    eligibility: { ok: boolean; problems: string[]; pageCount: number; pages: { width: number; height: number }[]; fillableFieldCount?: number };
    fileBase64: string | null;
    preparedBase64: string | null;
    liveFields: HrFormField[] | null;
    /** Oct 18 D1: fields of the LIVE setup saved before values set by HR were made read-only (empty when the setup is fine). */
    liveNeedsCorrection?: Array<{ fieldKey: string; label: string; message: string }>;
  };
}
export interface HrLiveSigningConfigSummary {
  templateId: string; templateVersionId: string; configId: string; configNumber: number; createdAt: string; pageCount: number;
  schemaVersion?: number; fieldCount?: number | null; assignmentPrompts?: HrAssignmentPrompt[]; assignmentSummary?: HrAssignmentSummary;
  /** Oct 18 D1: this live setup was saved before values set by HR were made read-only; it cannot be assigned until it is published again. */
  needsCorrection?: boolean;
}

export async function listHrLiveSigningConfigs(): Promise<{ configs: HrLiveSigningConfigSummary[] }> {
  return apiRequest('/hr/admin/document-signing-configs');
}
export async function getHrSigningSetup(templateId: string, versionId: string): Promise<HrSigningSetup> {
  return apiRequest(`/hr/admin/document-templates/${encodeURIComponent(templateId)}/versions/${encodeURIComponent(versionId)}/signing-setup`);
}
export async function saveHrSigningConfig(templateId: string, versionId: string, input: { placement: HrDocumentPlacement; scopeConfirmed: boolean }): Promise<{ config: HrSigningConfig; created: boolean; message: string }> {
  return apiRequest(`/hr/admin/document-templates/${encodeURIComponent(templateId)}/versions/${encodeURIComponent(versionId)}/signing-config`, { method: 'POST', body: JSON.stringify(input) });
}
/** Read-only: the stored PDF's fillable fields as a draft list for HR to review, plus an accurate prepared preview. Writes nothing. */
export async function importHrSigningFields(templateId: string, versionId: string, keep?: string[]): Promise<HrImportPreview> {
  return apiRequest(`/hr/admin/document-templates/${encodeURIComponent(templateId)}/versions/${encodeURIComponent(versionId)}/signing-import`, { method: 'POST', body: JSON.stringify(keep ? { keep } : {}) });
}
/** Publish a version-3 form setup. `reviewAttested` replaces the old "needs only a signature and date" confirmation. */
export async function saveHrFormSetup(
  templateId: string, versionId: string,
  input: HrFormSetupBody & { reviewAttested: boolean; importResolutions?: Record<string, HrImportResolution> },
): Promise<{ config: HrSigningConfig; created: boolean; message: string }> {
  return apiRequest(`/hr/admin/document-templates/${encodeURIComponent(templateId)}/versions/${encodeURIComponent(versionId)}/signing-config`, {
    method: 'POST', body: JSON.stringify({ schemaVersion: 3, ...input }),
  });
}
/** Oct 17: save (create or replace) the UNPUBLISHED draft. `expectedRevision` is null for the first save, otherwise the revision last received. */
export async function saveHrSigningDraft(
  templateId: string, versionId: string,
  input: HrFormSetupBody & { importResolutions: Record<string, HrImportResolution>; expectedRevision: number | null },
): Promise<{ draft: HrSigningDraft; message: string }> {
  return apiRequest(`/hr/admin/document-templates/${encodeURIComponent(templateId)}/versions/${encodeURIComponent(versionId)}/signing-draft`, {
    method: 'POST', body: JSON.stringify(input),
  });
}
/** Oct 17: remove the unpublished draft. The published setup is unchanged. */
export async function discardHrSigningDraft(templateId: string, versionId: string): Promise<{ discarded: boolean; message: string }> {
  return apiRequest(`/hr/admin/document-templates/${encodeURIComponent(templateId)}/versions/${encodeURIComponent(versionId)}/signing-draft/discard`, { method: 'POST', body: '{}' });
}
export async function retireHrSigningConfig(templateId: string, versionId: string): Promise<{ config: HrSigningConfig; message: string }> {
  return apiRequest(`/hr/admin/document-templates/${encodeURIComponent(templateId)}/versions/${encodeURIComponent(versionId)}/signing-config/retire`, { method: 'POST', body: '{}' });
}

// ═══════════════════════════════════════════════════════════
// HR API — Document history reasons (admin-managed list) + Move to History / Restore
// Authority: October 10 14:05/14:08 Phoenix addenda (migration 087).
// ═══════════════════════════════════════════════════════════

export interface HrDocumentHistoryReason {
  reasonId: string;
  code: string;
  label: string;
  explanation: string;
  isProtected: boolean;
  requiresSupportingDetail: boolean;
  retiredAt: string | null;
}

export async function listHrDocumentHistoryReasons(): Promise<{ reasons: HrDocumentHistoryReason[] }> {
  return apiRequest('/hr/admin/document-history-reasons');
}

export async function createHrDocumentHistoryReason(input: { label: string; explanation: string }): Promise<HrDocumentHistoryReason> {
  return apiRequest('/hr/admin/document-history-reasons', { method: 'POST', body: JSON.stringify(input) });
}

export async function editHrDocumentHistoryReason(reasonId: string, input: { label: string; explanation: string }): Promise<HrDocumentHistoryReason> {
  return apiRequest(`/hr/admin/document-history-reasons/${encodeURIComponent(reasonId)}/edit`, { method: 'POST', body: JSON.stringify(input) });
}

export async function retireHrDocumentHistoryReason(reasonId: string): Promise<{ reasonId: string; retired: boolean }> {
  return apiRequest(`/hr/admin/document-history-reasons/${encodeURIComponent(reasonId)}/retire`, { method: 'POST' });
}

export async function reactivateHrDocumentHistoryReason(reasonId: string): Promise<{ reasonId: string; reactivated: boolean }> {
  return apiRequest(`/hr/admin/document-history-reasons/${encodeURIComponent(reasonId)}/reactivate`, { method: 'POST' });
}

export interface HrDocumentHistoryEvent {
  eventId: string;
  assignmentId: string;
  reasonId: string;
  reasonCodeSnapshot: string;
  reasonLabelSnapshot: string;
  reasonExplanationSnapshot: string;
  detailText: string | null;
  linkedAssignmentId: string | null;
  equipmentReturnDate: string | null;
  equipmentReceivedBy: string | null;
  equipmentIdentified: string | null;
  equipmentWholeIssuanceConfirmed: boolean | null;
  filedInErrorConfirmed: boolean | null;
  actorEmployeeId: string;
  occurredAt: string;
  correctedAt: string | null;
  correctedByEmployeeId: string | null;
  correctionNote: string | null;
}

export interface MoveToHistoryInput {
  reasonId: string;
  detailText?: string | null;
  linkedAssignmentId?: string | null;
  equipmentReturnDate?: string | null;
  equipmentReceivedBy?: string | null;
  equipmentIdentified?: string | null;
  equipmentWholeIssuanceConfirmed?: boolean | null;
  filedInErrorConfirmed?: boolean | null;
}

export async function moveHrDocumentAssignmentToHistory(assignmentId: string, input: MoveToHistoryInput): Promise<HrDocumentHistoryEvent> {
  return apiRequest(`/hr/documents/${encodeURIComponent(assignmentId)}/move-to-history`, { method: 'POST', body: JSON.stringify(input) });
}

export async function restoreHrDocumentAssignmentFromHistory(assignmentId: string, correctionNote?: string): Promise<HrDocumentHistoryEvent> {
  return apiRequest(`/hr/documents/${encodeURIComponent(assignmentId)}/restore`, { method: 'POST', body: JSON.stringify({ correctionNote: correctionNote ?? null }) });
}

export async function listHrDocumentHistoryEvents(assignmentId: string): Promise<{ events: HrDocumentHistoryEvent[] }> {
  return apiRequest(`/hr/documents/${encodeURIComponent(assignmentId)}/history-events`);
}

export async function listHrEligibleReplacementCandidates(employeeId: string, excludeAssignmentId?: string): Promise<{ candidates: HrDocumentAssignment[] }> {
  const q = excludeAssignmentId ? `?exclude=${encodeURIComponent(excludeAssignmentId)}` : '';
  return apiRequest(`/hr/admin/employees/${encodeURIComponent(employeeId)}/documents/eligible-replacements${q}`);
}

export async function listHrChecklistTaskHistory(assignmentTaskId: string): Promise<{ history: Array<{ historyId: string; status: string; note: string | null; actorEmployeeId: string | null; changedByEmployeeId: string; changedAt: string }> }> {
  return apiRequest(`/hr/checklist-tasks/${encodeURIComponent(assignmentTaskId)}/history`);
}

export async function listHrPersonalDetailHistory(employeeId: string): Promise<{ history: Array<{ historyId: string; personalEmail: string | null; personalPhone: string | null; addressLine1: string | null; addressCity: string | null; addressState: string | null; addressZip: string | null; familyReferenceNote: string | null; changedByEmployeeId: string; changedAt: string }> }> {
  return apiRequest(`/hr/admin/employees/${encodeURIComponent(employeeId)}/personal/history`);
}

// ═══════════════════════════════════════════════════════════
// HR API — BUILD-042 simple benefits (migration 207). HR-managed benefit information + per-
// employee eligibility review (not_reviewed/eligible/not_eligible + optional date). Eligibility
// is NOT enrollment. NEEDS DEV DEPLOY: migration 207 + these routes are not deployed this cycle.
// ═══════════════════════════════════════════════════════════

export type HrBenefitEligibilityStatus = 'not_reviewed' | 'eligible' | 'not_eligible';

export interface HrBenefitPlanLink {
  linkId: string;
  label: string;
  url: string;
  sortOrder: number;
}
export interface HrBenefitPlan {
  planId: string;
  name: string;
  category: string | null;
  shortDescription: string | null;
  providerName: string | null;
  providerContact: string | null;
  externalUrl: string | null;
  documentTemplateId: string | null;
  published: boolean;
  sortOrder: number;
  /** Cycle 12 (migration 210): optional default, N CALENDAR days after the hire date (whole number 0..3650). null = no rule. A suggestion only. Optional only so older servers/fixtures type-check. */
  waitingPeriodDays?: number | null;
  createdAt: string;
  updatedAt: string;
  archivedAt: string | null;
  links: HrBenefitPlanLink[];
}

export interface HrBenefitPlanInput {
  name: string;
  category?: string | null;
  shortDescription?: string | null;
  providerName?: string | null;
  providerContact?: string | null;
  externalUrl?: string | null;
  documentTemplateId?: string | null;
  published?: boolean;
  sortOrder?: number;
  /** Cycle 12: whole number of CALENDAR days 0..3650, or null for no rule. On update, omit to leave it unchanged; null clears it. */
  waitingPeriodDays?: number | null;
  links?: Array<{ label: string; url: string }>;
}

/** One benefit as seen by an employee or by HR, joined with the relevant eligibility. A plan with
 *  no eligibility row reads as 'not_reviewed'. Admin responses additionally carry eligibilityId/
 *  note/statusChangedAt; the employee response omits those. */
export interface HrBenefitEntry {
  planId: string;
  name: string;
  category: string | null;
  shortDescription: string | null;
  providerName: string | null;
  providerContact: string | null;
  externalUrl: string | null;
  documentTemplateId: string | null;
  links: HrBenefitPlanLink[];
  published: boolean;
  status: HrBenefitEligibilityStatus;
  eligibilityDate: string | null;
  // Admin view only:
  eligibilityId?: string | null;
  note?: string | null;
  statusChangedAt?: string | null;
  /** Admin view only (cycle 12): the plan's HR-configured waiting-period default in CALENDAR days, or null for no rule. The employee view never carries it. */
  waitingPeriodDays?: number | null;
}

// ─── HR admin: benefit plan catalog (hr.benefit.manage, company scope) ─────────────────────────
export async function listHrBenefitPlans(includeArchived = false): Promise<{ plans: HrBenefitPlan[] }> {
  return apiRequest(`/hr/admin/benefit-plans${includeArchived ? '?includeArchived=true' : ''}`);
}
export async function createHrBenefitPlan(input: HrBenefitPlanInput): Promise<HrBenefitPlan> {
  return apiRequest('/hr/admin/benefit-plans', { method: 'POST', body: JSON.stringify(input) });
}
export async function updateHrBenefitPlan(planId: string, input: Partial<HrBenefitPlanInput>): Promise<HrBenefitPlan> {
  return apiRequest(`/hr/admin/benefit-plans/${encodeURIComponent(planId)}/update`, { method: 'POST', body: JSON.stringify(input) });
}
export async function archiveHrBenefitPlan(planId: string): Promise<{ planId: string; archived: boolean }> {
  return apiRequest(`/hr/admin/benefit-plans/${encodeURIComponent(planId)}/archive`, { method: 'POST' });
}
export async function restoreHrBenefitPlan(planId: string): Promise<{ planId: string; archived: boolean }> {
  return apiRequest(`/hr/admin/benefit-plans/${encodeURIComponent(planId)}/restore`, { method: 'POST' });
}

// ─── HR admin: per-employee eligibility ───────────────────────────────────────────────────────
/** Oct 23: the minimal person picker for a benefit manager (name only; people with an HR record, not archived). Needs hr.benefit.manage, NOT hr.org.manage. */
export async function listHrBenefitEligibilityPeople(): Promise<{ people: Array<{ employeeId: string; name: string }> }> {
  return apiRequest('/hr/admin/benefit-eligibility/people');
}

export async function listHrEmployeeBenefits(employeeId: string): Promise<{ benefits: HrBenefitEntry[] }> {
  return apiRequest(`/hr/admin/employees/${encodeURIComponent(employeeId)}/benefits`);
}
export async function setHrEmployeeBenefitEligibility(
  employeeId: string, planId: string,
  input: { status: HrBenefitEligibilityStatus; eligibilityDate?: string | null; note?: string | null }
): Promise<{ eligibilityId: string; employeeId: string; planId: string; status: HrBenefitEligibilityStatus; eligibilityDate: string | null; note: string | null; actorEmployeeId: string | null; statusChangedAt: string | null }> {
  return apiRequest(`/hr/admin/employees/${encodeURIComponent(employeeId)}/benefits/${encodeURIComponent(planId)}`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}
export async function listHrBenefitEligibilityHistory(eligibilityId: string): Promise<{ history: Array<{ historyId: string; status: string; eligibilityDate: string | null; note: string | null; actorEmployeeId: string | null; changedByEmployeeId: string; changedAt: string }> }> {
  return apiRequest(`/hr/admin/benefit-eligibility/${encodeURIComponent(eligibilityId)}/history`);
}

// ─── Employee self-view: published benefit info + own eligibility (subject match, no capability) ─
export async function getMyHrBenefits(): Promise<{ benefits: HrBenefitEntry[] }> {
  return apiRequest('/hr/me/benefits');
}

// ═══════════════════════════════════════════════════════════
// HR API — BUILD-042 employee lifecycle (migration 208): scheduled departure, roster archive / restore, rehire, and the offboarding view.
// All of it needs hr.lifecycle.manage (company) on the server; a caller without it gets 404 on the view and 403 on writes. NEEDS DEV DEPLOY.
// None of it changes account access: account status is read-only here, with a link to Settings.
// ═══════════════════════════════════════════════════════════

export type HrEmploymentLifecycleState = 'active' | 'departure_scheduled' | 'separated' | 'no_open_period';

export interface HrOffboarding {
  offboardingId: string;
  employeeId: string;
  periodId: string;
  lastWorkingDate: string;
  checklistAssignmentId: string | null;
  note: string | null;
  startedByEmployeeId: string;
  startedAt: string;
  cancelledAt: string | null;
  cancelledByEmployeeId: string | null;
  cancelNote: string | null;
}
export interface HrLifecyclePeriod { periodNumber: number; hireDate: string; separationDate: string | null; status: string }
export interface HrLifecycleTaskItem {
  assignmentTaskId: string; assignmentId: string; checklist: string; processKind: 'onboarding' | 'offboarding'; task: string;
  responsibleRole: string; dueDate: string; status: string; fromThisDeparture: boolean;
}
export interface HrLifecycleDocumentItem { assignmentId: string; form: string; versionNumber: number; status: string; signatureRequestPending: boolean; assignedAt: string }
export interface HrLifecycleEquipmentItem {
  itemKey: string; checkoutId: string; assignmentId: string; position: number; equipmentType: string; description: string; model: string | null; serialOrAsset: string | null;
  issueDate: string; receiptState: 'awaiting_return' | 'awaiting_hr_review' | 'needs_correction' | 'reviewed_complete' | 'cancelled'; movedToHistory: boolean;
  returned: null | { eventId: string; returnDate: string | null; receivedBy: string | null; identified: string | null; recordedAt: string };
  canRecordReturn: boolean;
}
export type HrLifecycleSection<T> = { visible: true; items: T[]; count?: number } | { visible: false; reason: string };

export interface HrEmployeeLifecycle {
  employeeId: string;
  name: string | null;
  todayPhoenix: string;
  employment: {
    state: HrEmploymentLifecycleState;
    openPeriod: { periodId: string; periodNumber: number; hireDate: string } | null;
    periods: HrLifecyclePeriod[];
    departure: HrOffboarding | null;
    departureHistory: HrOffboarding[];
  };
  archive: { archived: boolean; events: Array<{ eventId: string; eventNumber: number; action: 'archive' | 'restore'; note: string | null; actorEmployeeId: string; occurredAt: string }> };
  account: { available: true; globalStatus: 'active' | 'disabled' | null; uniquehqAllowed: boolean; settingsPath: string; changeNote: string } | { available: false; reason: string };
  outstanding: {
    tasks: HrLifecycleSection<HrLifecycleTaskItem>;
    documents: HrLifecycleSection<HrLifecycleDocumentItem>;
    equipment: ({ visible: true; items: HrLifecycleEquipmentItem[]; outstandingCount: number; returnedCount: number; rules: string[] }) | { visible: false; reason: string };
  };
}

export async function getHrEmployeeLifecycle(employeeId: string): Promise<HrEmployeeLifecycle> {
  return apiRequest(`/hr/admin/employees/${encodeURIComponent(employeeId)}/lifecycle`);
}
export async function startHrOffboarding(employeeId: string, input: { lastWorkingDate: string; checklistTemplateId?: string | null; note?: string | null }): Promise<{ offboarding: HrOffboarding; state: HrEmploymentLifecycleState }> {
  const r = await apiRequest(`/hr/admin/employees/${encodeURIComponent(employeeId)}/offboarding`, { method: 'POST', body: JSON.stringify(input) });
  invalidateHrAdminEmployeesCache();
  return r;
}
export async function cancelHrOffboarding(offboardingId: string, input: { note?: string | null }): Promise<{ offboarding: HrOffboarding; state: HrEmploymentLifecycleState; preserved: string }> {
  const r = await apiRequest(`/hr/admin/offboarding/${encodeURIComponent(offboardingId)}/cancel`, { method: 'POST', body: JSON.stringify(input) });
  invalidateHrAdminEmployeesCache();
  return r;
}
export async function archiveHrEmployee(employeeId: string, input: { note?: string | null }): Promise<{ archived: boolean; unchanged: string }> {
  const r = await apiRequest(`/hr/admin/employees/${encodeURIComponent(employeeId)}/archive`, { method: 'POST', body: JSON.stringify(input) });
  invalidateHrAdminEmployeesCache();
  return r;
}
export async function restoreHrEmployee(employeeId: string, input: { note?: string | null }): Promise<{ archived: boolean; unchanged: string }> {
  const r = await apiRequest(`/hr/admin/employees/${encodeURIComponent(employeeId)}/restore`, { method: 'POST', body: JSON.stringify(input) });
  invalidateHrAdminEmployeesCache();
  return r;
}
export async function rehireHrEmployee(employeeId: string, input: { hireDate: string; title?: string | null; departmentId?: string | null; managerEmployeeId?: string | null; classification?: string | null }): Promise<{ newPeriodId: string; unchanged: string }> {
  const r = await apiRequest(`/hr/admin/employees/${encodeURIComponent(employeeId)}/rehire`, { method: 'POST', body: JSON.stringify(input) });
  invalidateHrAdminEmployeesCache();
  return r;
}

// ─── Oct 24: guided initial HR setup (migration 209, LOCAL) ──────────────────────────────────────────────────────────────────────
// Only a record initiated through POST /hr/admin/employees/:id/record has progress; every other record reads progress: null (grandfathered).
export type HrSetupStep = 'details' | 'benefits' | 'checklist' | 'documents' | 'equipment' | 'review';
export type HrSetupStatus = 'in_progress' | 'finished';
/** The roster's compact view of a record's setup (HrAdminEmployeeListEntry.setup). */
export interface HrSetupRosterState { status: HrSetupStatus; step: HrSetupStep; updatedAt: string }
/** HR's explicit step decisions - review/applicability statements only. They cancel, complete and issue nothing. */
export interface HrSetupDecisions {
  details?: { saved: true };
  benefits?: { reviewed: true };
  checklist?: { decision: 'not_applicable' };
  documents?: { decision: 'not_applicable' };
  equipment?: { decision: 'no_equipment_needed' };
}
/** Cycle 12: where a step stands, derived by the SERVER from saved authoritative data + HR's flags. Navigation and an empty list never make a step reviewed or "not needed". */
/** 'deferred' is LEGACY tolerance only (cycle 12 "Come back later", removed in cycle 13): the server never emits it and the UI treats a received one as needs_attention. */
export type HrSetupStepStatus = 'unvisited' | 'needs_attention' | 'deferred' | 'configured' | 'not_needed';
/** `deferred` is a legacy stored flag (read-only; nothing writes it any more). */
export interface HrSetupStepFlags { visited?: boolean; deferred?: boolean }
export interface HrSetupProgress {
  status: HrSetupStatus;
  step: HrSetupStep;
  decisions: HrSetupDecisions;
  /** Cycle 12: per-step visited flags (+ the legacy deferred flag, read-only) (stored inside the existing decisions JSON; no schema change). Always sent by the server; optional only for older mocks. */
  flags?: Partial<Record<HrSetupStep, HrSetupStepFlags>>;
  startedAt: string;
  startedByEmployeeId: string;
  updatedAt: string;
  updatedByEmployeeId: string;
  finishedAt: string | null;
  finishedByEmployeeId: string | null;
}
/** An area the CALLER may not read: never empty and never "not applicable". `reason` is e.g. "requires hr.checklist.assign". */
export interface HrSetupAreaUnavailable { state: 'unavailable'; reason: string; capability: string | null }
export interface HrSetupSummary {
  details: {
    hireDate: string | null; title: string | null; departmentId: string | null; managerEmployeeId: string | null; classification: string | null;
    phone: string | null; phoneAvailable: boolean;
  };
  benefits: HrSetupAreaUnavailable | {
    state: 'available'; planCount: number; reviewedCount: number; eligibleCount: number; notEligibleCount: number;
    /** Cycle 12: the applicable plans (active + published) HR must decide; `decidedCount` counts eligible/not_eligible with a valid date. */
    applicableCount?: number; decidedCount?: number; hireDate?: string | null;
    items: Array<{ planId: string; name: string; status: HrBenefitEligibilityStatus; eligibilityDate: string | null; waitingPeriodDays?: number | null }>;
  };
  checklist: HrSetupAreaUnavailable | {
    state: 'available'; onboardingCount: number;
    assignments: Array<{ assignmentId: string; templateName: string; processKind: 'onboarding' | 'offboarding'; anchorDate: string; assignedAt: string; taskCount: number; completedCount: number }>;
  };
  documents: HrSetupAreaUnavailable | {
    state: 'available'; liveCount: number;
    assignments: Array<{ assignmentId: string; templateName: string; versionNumber: number; status: string; assignedAt: string }>;
  };
  equipment: HrSetupAreaUnavailable | {
    state: 'available'; checkouts: Array<{ checkoutId: string; status: 'draft' | 'generated'; itemCount: number }>;
  };
  /** Cycle 12 (server-derived; always sent, optional only for older mocks): per-step standing, whether Finish is possible, and what blocks it. */
  steps?: Record<Exclude<HrSetupStep, 'review'>, { status: HrSetupStepStatus; reason: string | null }>;
  canFinish?: boolean;
  blockers?: Array<{ step: Exclude<HrSetupStep, 'review'>; status: HrSetupStepStatus; reason: string }>;
  /** Whether the "nothing needed" acknowledgment may be offered for the area right now (false while conflicting records exist or the area is unreadable). */
  noneNeeded?: Record<'benefits' | 'checklist' | 'documents' | 'equipment', { allowed: boolean; reason: string | null }>;
}
export interface HrSetupView { employeeId: string; progress: HrSetupProgress | null; summary: HrSetupSummary }
/** Body of a save. Decisions: a key set to null clears that decision; unknown keys are refused (400). */
export interface HrSetupSaveBody {
  step: HrSetupStep;
  decisions?: { [K in keyof HrSetupDecisions]?: HrSetupDecisions[K] | null };
  nextStep?: HrSetupStep;
  /** Cycle 12: mark `step` visited (a visit alone resolves nothing). */
  visited?: boolean;
  /** @deprecated Cycle 13 removed "Come back later". The server tolerates and IGNORES this field from an old client; nothing in the UI sends it. */
  deferred?: boolean;
}
/** 409 SETUP_INCOMPLETE and 403 (an area the caller cannot read) both carry `missing`; the thrown error's body shape follows apiRequest. */
export interface HrSetupMissing { step: HrSetupStep; reason: string; capability?: string | null }

export async function getHrSetup(employeeId: string): Promise<HrSetupView> {
  return apiRequest(`/hr/admin/employees/${encodeURIComponent(employeeId)}/setup`);
}
export async function saveHrSetup(employeeId: string, body: HrSetupSaveBody): Promise<{ progress: HrSetupProgress; unchanged: boolean }> {
  const r = await apiRequest(`/hr/admin/employees/${encodeURIComponent(employeeId)}/setup/save`, { method: 'POST', body: JSON.stringify(body) });
  invalidateHrAdminEmployeesCache();
  return r;
}
export async function finishHrSetup(employeeId: string): Promise<{ progress: HrSetupProgress; unchanged: string }> {
  const r = await apiRequest(`/hr/admin/employees/${encodeURIComponent(employeeId)}/setup/finish`, { method: 'POST' });
  invalidateHrAdminEmployeesCache();
  return r;
}
